import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  TextField,
  CircularProgress,
  alpha,
  useTheme,
  Stack,
  Button,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  Dialog,
  DialogContent,
  Divider,
  Avatar,
  Popover,
  Skeleton,
  Badge,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import TuneIcon from '@mui/icons-material/Tune';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PrintIcon from '@mui/icons-material/Print';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import FlagIcon from '@mui/icons-material/Flag';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useDispatch } from 'react-redux';
import * as actions from '../../redux/actions';
import { JOB_FILTER_DEF, computeCommitment } from '../panel/JobFilterPanel';
import PendingItemsModal, { PAID_COLOR } from '../Modal/PendingItemsModal';

// ── Status map ───────────────────────────────────────────────────────────────

const STATUS_MAP = {
  PENDING: {
    label: 'รอดำเนินการ',
    short: 'รอดำเนินการ',
    color: '#64748b',
    bg: '#f1f5f9',
  },
  IN_PROGRESS: {
    label: 'กำลังดำเนินการ',
    short: 'ดำเนินการ',
    color: '#2563eb',
    bg: '#eff6ff',
  },
  DELIVERY: {
    label: 'งาน100%รอรับเงินงวดสุดท้าย',
    short: 'รอรับเงิน',
    color: '#d97706',
    bg: '#fffbeb',
  },
  Warranty: {
    label: 'มีWarrant/Guarantee',
    short: 'Warranty',
    color: '#7c3aed',
    bg: '#f5f3ff',
  },
  WAITING_PAYMENT: {
    label: 'รอรับชำระเงิน',
    short: 'รอชำระ',
    color: '#c2410c',
    bg: '#fff7ed',
  },
  ELIGIBLE_FOR_CLOSE: {
    label: 'พร้อมปิดงาน',
    short: 'พร้อมปิด',
    color: '#0369a1',
    bg: '#e0f2fe',
  },
  COMPLETED: {
    label: 'ไม่มีภาระผูกพัน',
    short: 'ปิดงาน',
    color: '#059669',
    bg: '#ecfdf5',
  },
};
const STATUS_KEYS = Object.keys(STATUS_MAP);

// ── operation_status → dash_* fields mapping ─────────────────────────────────
// เมื่อเปลี่ยน col 2 → auto-fill สถานภาพภายในโครงการ
// dash_commitment คำนวณ auto จาก computeCommitment() ไม่ต้องระบุ
const STATUS_TO_DASH = {
  PENDING: {
    dash_work: '',
    dash_delivery: 'none',
    dash_disbursement: 'none',
    dash_warranty: 'none',
    dash_billing: 'none',
    dash_payment: 'waiting',
  },
  IN_PROGRESS: {
    dash_work: '',
    dash_delivery: 'partial',
    dash_disbursement: 'has',
    dash_warranty: 'none',
    dash_billing: 'none',
    dash_payment: 'waiting',
  },
  DELIVERY: {
    dash_work: 'done',
    dash_delivery: 'done',
    dash_disbursement: 'has',
    dash_warranty: 'none',
    dash_billing: 'partial',
    dash_payment: 'waiting',
  },
  Warranty: {
    dash_work: 'done',
    dash_delivery: 'done',
    dash_disbursement: 'none',
    dash_warranty: 'warranty',
    dash_billing: 'done',
    dash_payment: 'partial',
  },
  WAITING_PAYMENT: {
    dash_work: 'done',
    dash_delivery: 'done',
    dash_disbursement: 'none',
    dash_warranty: 'none',
    dash_billing: 'done',
    dash_payment: 'waiting',
  },
  ELIGIBLE_FOR_CLOSE: {
    dash_work: 'done',
    dash_delivery: 'done',
    dash_disbursement: 'none',
    dash_warranty: 'expired',
    dash_billing: 'done',
    dash_payment: 'received',
  },
  COMPLETED: {
    dash_work: 'done',
    dash_delivery: 'done',
    dash_disbursement: 'none',
    dash_warranty: 'none',
    dash_billing: 'done',
    dash_payment: 'received',
  },
};

// ── 3-Dimension Status System ─────────────────────────────────────────────────

const WORK_OPTIONS = [
  { key: 'waiting', label: 'รอเข้าดำเนินการ', color: '#64748b' },
  { key: 'in_progress', label: 'ดำเนินการ', color: '#2563eb' },
  { key: 'done', label: 'เสร็จ 100%', color: '#059669' },
];

const FINANCE_OPTIONS = [
  { key: 'none', label: 'ยังไม่เคยเบิก', color: '#94a3b8' },
  { key: 'partial', label: 'เบิกแล้วบางส่วน', color: '#f59e0b' },
  { key: 'billed_waiting', label: 'วางบิลแล้วรอรับเงิน', color: '#d97706' },
  { key: 'last_installment', label: 'รอรับงวดสุดท้าย', color: '#c2410c' },
  { key: 'completed_po', label: 'รับเงิน100%แล้ว', color: '#059669' },
];

const BG_USAGE_OPTIONS = [
  { key: 'no_bg_usage', label: 'ไม่ใช้ BG แล้ว', color: '#374151' },
];
const NO_BG_USAGE_KEY = 'no_bg_usage';

const LEGAL_OPTIONS = [
  { key: 'legal_warranty', label: 'Warrant/Guarantee', color: '#7c3aed' },
  { key: 'legal_debt', label: 'เจ้าหนี้ค้างจ่าย', color: '#dc2626' },
  { key: 'legal_lawsuit', label: 'ฟ้องร้อง', color: '#991b1b' },
];

function computeHasCommitment(row) {
  return (
    row.work_status !== 'done' ||
    row.finance_status !== 'completed_po' ||
    row.legal_warranty ||
    row.legal_debt ||
    row.legal_lawsuit
  );
}

function isBgUsageDisabled(row) {
  return row?.bg_usage_category === NO_BG_USAGE_KEY;
}

function getBgColumnValues(row, inclBgRemain = true) {
  const bgUsageDisabled = isBgUsageDisabled(row);
  const col32 = Number(row?.ap_pending_unpaid) || 0;
  // col15 "BG คงเหลือ" = col10 (ทุนคาดการณ์) - col35 (รอจ่าย) - col32 (AP รอจ่าย) + col34 (รอเงินคืน)
  const col15 = Math.max(
    getProjectedFund(row) - getExpectedValue(row) - col32 + getRefundPendingValue(row),
    0,
  );
  const col16 = Number(row?.to_spend) || 0;
  const col17 = col32 + (bgUsageDisabled && !inclBgRemain ? 0 : col15) + col16;
  return { bgUsageDisabled, col32, col15, col16, col17 };
}

// col 11 "ใช้ไปแล้ว" = ยอด Paid เท่านั้น (expenses ที่ status === 'SUCCESS')
// เหมือนแถวรวมคอลัมน์ "Paid" ใน BudgetTable.js (renderCost) เป๊ะ — BudgetTable รวมยอดผ่าน budget
// ของโปรเจกต์เท่านั้น (ไม่ใช่ expenses ทั้งหมดที่ project ตรงกัน) เลยต้องกรอง e.budget ให้ตรงกับ
// budget จริงของโปรเจกต์นี้ด้วย ไม่งั้นถ้ามี Expense ที่ budget เป็น null/ผูกผิดโปรเจกต์ (data ไม่สมบูรณ์)
// ตัวเลขจะไม่ตรงกับ BudgetTable ทันที (ไล่แก้ data ทีละเคสไม่จบ เพราะเป็นบั๊กที่จุดสร้าง Expense เอง)
function getPaidValue(row) {
  const budgetIds = new Set(
    (row?.budget || []).map((b) => String(b?._id || b?.id)),
  );
  return (row?.expenses || []).reduce(
    (acc, e) =>
      acc +
      (e?.status === 'SUCCESS' && budgetIds.has(String(e?.budget))
        ? Number(e?.price) || 0
        : 0),
    0,
  );
}

// col 35 "รอจ่าย" = ยอด expense ที่ยังไม่จ่าย/ไม่ถูกปฏิเสธ (status ไม่ใช่ SUCCESS และไม่ใช่ REJECT)
// เหมือนคอลัมน์ "Expected" ใน BudgetTable.js (renderCostApprove) เป๊ะ — กรอง e.budget ให้ตรงกับ budget
// จริงของโปรเจกต์นี้เหมือน getPaidValue ด้านบน (เหตุผลเดียวกัน)
function getExpectedValue(row) {
  const budgetIds = new Set(
    (row?.budget || []).map((b) => String(b?._id || b?.id)),
  );
  return (row?.expenses || []).reduce(
    (acc, e) =>
      acc +
      (e?.status !== 'SUCCESS' &&
      e?.status !== 'REJECT' &&
      budgetIds.has(String(e?.budget))
        ? Number(e?.price) || 0
        : 0),
    0,
  );
}

// col 10 "ทุนคาดการณ์" = col 33 (BG / total_budget) - col 11 (ใช้ไปแล้ว / Paid)
function getProjectedFund(row) {
  return (Number(row?.total_budget) || 0) - getPaidValue(row);
}

// col 34 "รอเงินคืน" = ผลรวม refund_items ที่ยังไม่คืน (refunded !== true) ของทุก budget ในโปรเจกต์นี้
// เหมือนคอลัมน์ "รอเงินคืน" ใน BudgetTable.js (renderRefundPending) รวมทุก budget
function getRefundPendingValue(row) {
  return (row?.budget || []).reduce((acc, b) => {
    const unrefunded = (b?.refund_items || []).filter((i) => !i?.refunded);
    return (
      acc + unrefunded.reduce((s, i) => s + (Number(i?.amount) || 0), 0)
    );
  }, 0);
}

// ใช้ไปจริง (Paid + Expected สด จาก getPaidValue/getExpectedValue) + AP รอจ่าย (pending_items ยังไม่จ่าย)
// - เงินรอคืน (refund_items ยังไม่คืน) = สูตร totalExpenses ใน ProjectPopup.js (ตัวเลข "จ่ายแล้ว" ในการ์ด
// สรุป / Grand Total ของ BudgetTable.js) — เดิมอ่านจาก row.total_expenses (คำนวณสดฝั่ง backend ก็จริง
// แต่ไม่กรอง e.budget ให้ตรงกับ budget จริงของโปรเจกต์เหมือน getPaidValue/getExpectedValue เลยรวม
// expense ที่ budget เป็น null/ผูกผิดไปด้วย ทำให้ไม่ตรงกับ ProjectPopup.js ที่รวมผ่านแต่ละ budget เท่านั้น)
// ใช้เป็นส่วนประกอบของ col 19 "กำไรคาดการณ์" (getEstimatedProfit ด้านล่าง) เท่านั้น ไม่ใช่ col 11 แล้ว
function getSpentValue(row) {
  return (
    getPaidValue(row) +
    getExpectedValue(row) +
    (Number(row?.ap_pending_unpaid) || 0) -
    getRefundPendingValue(row)
  );
}

// col 19 "กำไรคาดการณ์" — สูตรเดียวกับ estimatedProfitForPercent ใน ProjectPopup.js
// = cost - จ่ายแล้ว(getSpentValue) + (ถ้าปิดใช้ BG แล้ว บวกผลรวมงบเหลือเป็นบวกของแต่ละ budget กลับเข้าไป)
function getEstimatedProfit(row) {
  // group row.expenses (flat, ระดับโปรเจกต์) ตาม e.budget ไว้ก่อน จะได้คำนวณ paid/expected สดต่อ budget
  // แทนการอ่าน b.total_expenses ที่เป็น field cache ฝั่ง backend (sync ไม่ทันทุก path เลยค้างได้)
  const expensesByBudget = new Map();
  (row?.expenses || []).forEach((e) => {
    const bid = e?.budget ? String(e.budget) : null;
    if (!bid) return;
    if (!expensesByBudget.has(bid)) expensesByBudget.set(bid, []);
    expensesByBudget.get(bid).push(e);
  });

  let positiveRemainingSum = 0;
  (row?.budget || []).forEach((b) => {
    const budgetExpenses = expensesByBudget.get(String(b?._id || b?.id)) || [];
    const paid = budgetExpenses.reduce(
      (s, e) => s + (e?.status === 'SUCCESS' ? Number(e?.price) || 0 : 0),
      0,
    );
    const expected = budgetExpenses.reduce(
      (s, e) =>
        s +
        (e?.status !== 'SUCCESS' && e?.status !== 'REJECT'
          ? Number(e?.price) || 0
          : 0),
      0,
    );
    const apPending = (b?.pending_items || [])
      .filter((i) => !i?.paid)
      .reduce((s, i) => s + (Number(i?.amount) || 0), 0);
    const refundPending = (b?.refund_items || [])
      .filter((i) => !i?.refunded)
      .reduce((s, i) => s + (Number(i?.amount) || 0), 0);
    const grandTotal = paid + expected + apPending - refundPending;
    const remaining = (Number(b?.cost) || 0) - grandTotal;
    if (remaining > 0) positiveRemainingSum += remaining;
  });
  return (
    (Number(row?.cost) || 0) -
    getSpentValue(row) +
    (isBgUsageDisabled(row) ? positiveRemainingSum : 0)
  );
}

const FILTER_META = {
  has_commitment: { label: 'มีภาระผูกพัน', color: '#d97706' },
  no_commitment: { label: 'ไม่มีภาระผูกพัน', color: '#059669' },
  col9_positive: { label: 'JS · มีภาระ · col9>0', color: '#2563eb' },
  col7_positive: { label: 'JS · มีภาระ · col7>0', color: '#059669' },
  ...Object.fromEntries(
    WORK_OPTIONS.map((o) => [
      `work_${o.key}`,
      { label: `งาน: ${o.label}`, color: o.color },
    ]),
  ),
  ...Object.fromEntries(
    FINANCE_OPTIONS.map((o) => [
      `finance_${o.key}`,
      { label: `การเงิน: ${o.label}`, color: o.color },
    ]),
  ),
  ...Object.fromEntries(
    BG_USAGE_OPTIONS.map((o) => [
      `bg_${o.key}`,
      { label: `งบ: ${o.label}`, color: o.color },
    ]),
  ),
  ...Object.fromEntries(
    LEGAL_OPTIONS.map((o) => [o.key, { label: o.label, color: o.color }]),
  ),
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getGroupOrder(projectNumber) {
  if (!projectNumber) return 3;
  if (/^JSID_/i.test(projectNumber)) return 1;
  if (/^JS_/i.test(projectNumber)) return 0;
  return 2;
}

function sortProjects(rows) {
  return [...(rows || [])].sort((a, b) => {
    const ga = getGroupOrder(a.project_number);
    const gb = getGroupOrder(b.project_number);
    if (ga !== gb) return ga - gb;
    const cmp = (a.project_number || '').localeCompare(
      b.project_number || '',
      'th',
      { numeric: true },
    );
    // JS_ (group 0) → descending (newest first); JSID_ + others → ascending
    return ga === 0 ? -cmp : cmp;
  });
}

const fmt = (n) => {
  const num = n == null ? 0 : Number(n);
  if (num === 0) return '-';
  return num.toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtPct = (n) => (n == null ? '0.00%' : `${Number(n).toFixed(2)}%`);

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  } catch {
    return null;
  }
};

const toDateInput = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const NumCell = React.memo(function NumCell({
  value,
  redWhenNegative = false,
  redWhenPositive = false,
  bold = false,
}) {
  const num = Number(value) || 0;
  const isNeg = num < 0;
  const color =
    redWhenNegative && isNeg
      ? 'error.main'
      : redWhenPositive && num > 0
      ? 'error.main'
      : 'text.primary';
  return (
    <Typography
      variant="caption"
      sx={{
        color,
        fontWeight: bold ? 600 : 400,
        whiteSpace: 'nowrap',
        textAlign: 'right',
        display: 'block',
        fontSize: '0.91rem',
      }}
    >
      {fmt(num)}
    </Typography>
  );
});

const PctCell = React.memo(function PctCell({
  value,
  redWhenNegative = false,
  redWhenOver = null,
  orangeWhenOver = null,
}) {
  const num = Number(value) || 0;
  const overLimit = redWhenOver !== null && num > redWhenOver;
  const overOrange =
    !overLimit && orangeWhenOver !== null && num > orangeWhenOver;
  const color = overLimit
    ? '#dc2626'
    : overOrange
    ? '#d97706'
    : redWhenNegative && num < 0
    ? 'error.main'
    : num > 0
    ? 'success.dark'
    : 'text.secondary';
  return (
    <Typography
      variant="caption"
      sx={{
        color,
        textAlign: 'right',
        whiteSpace: 'nowrap',
        display: 'block',
        fontWeight: overLimit ? 700 : 400,
        fontSize: '0.91rem',
      }}
    >
      {fmtPct(num)}
    </Typography>
  );
});

// ── Column header ─────────────────────────────────────────────────────────────

const TH_SX = {
  bgcolor: '#bae6fd',
  color: '#1e3a5f',
  fontWeight: 600,
  fontSize: '0.9rem',
  lineHeight: 1.3,
  letterSpacing: '0.01em',
  textAlign: 'center',
  py: 1.1,
  px: 1,
  borderRight: '1px solid rgba(30,58,95,0.1)',
  borderBottom: '2px solid rgba(30,58,95,0.18)',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  verticalAlign: 'middle',
  cursor: 'default',
  overflow: 'hidden',
};

const TH_HAS_TOT = { verticalAlign: 'top', pb: '22px' };

const TH_GROUP_SX = {
  ...TH_SX,
  bgcolor: '#87bbb7',
  color: '#0d3d39',
  borderRight: '1px solid rgba(13,61,57,0.15)',
  borderBottom: '2px solid rgba(13,61,57,0.2)',
};

const TH_GROUP2_SX = {
  ...TH_SX,
  bgcolor: '#fde8df',
  color: '#7c2d12',
  borderRight: '1px solid rgba(124,45,18,0.12)',
  borderBottom: '2px solid rgba(124,45,18,0.2)',
};

const TH_GROUP3_SX = {
  ...TH_SX,
  bgcolor: '#fef9c3',
  color: '#713f12',
  borderRight: '1px solid rgba(113,63,18,0.12)',
  borderBottom: '2px solid rgba(113,63,18,0.2)',
};

const TH_GROUP4_SX = {
  ...TH_SX,
  bgcolor: '#d1fae5',
  color: '#065f46',
  borderRight: '1px solid rgba(6,95,70,0.12)',
  borderBottom: '2px solid rgba(6,95,70,0.2)',
};

const TD_SX = {
  py: 0.7,
  px: 1,
  borderRight: '1px solid rgba(203,213,225,0.5)',
  borderBottom: '1px solid rgba(203,213,225,0.4)',
  verticalAlign: 'middle',
};

const TOT_BASE = {
  py: 0.8,
  px: 1,
  verticalAlign: 'middle',
  overflow: 'hidden',
  borderTop: '2px solid rgba(0,0,0,0.09)',
};
const TOT_SX = { ...TOT_BASE, bgcolor: '#eef2ff' }; // indigo (col 0-6, 26)
const TOT_GRP = {
  ...TOT_BASE,
  bgcolor: '#d9f0ee',
  borderRight: '1px solid rgba(13,61,57,0.18)',
}; // teal   (col 7-9)
const TOT_GRP2 = {
  ...TOT_BASE,
  bgcolor: '#fde8df',
  borderRight: '1px solid rgba(124,45,18,0.13)',
}; // peach  (col 10-14)
const TOT_GRP3 = {
  ...TOT_BASE,
  bgcolor: '#fef9c3',
  borderRight: '1px solid rgba(113,63,18,0.13)',
}; // yellow (col 15-20)
const TOT_GRP4 = {
  ...TOT_BASE,
  bgcolor: '#d1fae5',
  borderRight: '1px solid rgba(6,95,70,0.13)',
}; // mint  (col 27-30)

// ── Inline editable cell ──────────────────────────────────────────────────────

function InlineNumberCell({
  projectId,
  field,
  value,
  onSaved,
  isAdmin,
  getExtraPatch,
}) {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();
  const cancelRef = useRef(false);
  const committedRef = useRef(false);

  const startEdit = () => {
    if (!isAdmin) return;
    cancelRef.current = false;
    committedRef.current = false;
    setDraft(String(value ?? 0));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const handleSave = async (val) => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    if (committedRef.current) return;
    committedRef.current = true;
    const num = Number(val);
    const extra = getExtraPatch ? getExtraPatch(num) : {};
    setSaving(true);
    try {
      await dispatch(
        actions.projectPatchDashboard(projectId, { [field]: num, ...extra }),
      );
      onSaved(num, extra);
    } catch (_) {
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        value={draft}
        type="number"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSave(draft);
          }
          if (e.key === 'Escape') {
            cancelRef.current = true;
            setEditing(false);
          }
        }}
        onBlur={(e) => handleSave(e.target.value)}
        sx={{
          width: 70,
          '& input': {
            py: 0.3,
            px: 0.5,
            fontSize: '0.96rem',
            textAlign: 'right',
          },
        }}
        inputProps={{ min: 0, max: 100 }}
        disabled={saving}
      />
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="center"
      onClick={isAdmin ? startEdit : undefined}
      sx={{
        cursor: isAdmin ? 'pointer' : 'default',
        '&:hover .edit-icon': isAdmin ? { opacity: 1 } : {},
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: value > 0 ? 'primary.main' : 'text.secondary',
        }}
      >
        {Number(value || 0).toFixed(0)}%
      </Typography>
      {isAdmin && (
        <EditIcon
          className="edit-icon"
          sx={{
            fontSize: 12,
            opacity: 0,
            color: 'text.disabled',
            transition: 'opacity 0.15s',
          }}
        />
      )}
    </Stack>
  );
}

function InlineNoteCell({ projectId, value, onSaved, isAdmin }) {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const cancelRef = useRef(false);

  const startEdit = () => {
    if (!isAdmin) return;
    cancelRef.current = false;
    setDraft(value || '');
    setEditing(true);
  };

  const handleBlur = async (e) => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    const val = e.target.value;
    setSaving(true);
    try {
      await dispatch(
        actions.projectPatchDashboard(projectId, { dashboard_note: val }),
      );
      onSaved(val);
    } catch (_) {
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <TextField
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            cancelRef.current = true;
            setEditing(false);
          }
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        onBlur={handleBlur}
        sx={{
          width: 140,
          '& input': { py: 0.3, px: 0.5, fontSize: '0.94rem' },
        }}
        disabled={saving}
        autoFocus
      />
    );
  }

  return (
    <Box
      onClick={isAdmin ? startEdit : undefined}
      sx={{ cursor: isAdmin ? 'pointer' : 'default', minWidth: 80 }}
    >
      <Typography
        variant="caption"
        color={value ? 'text.primary' : 'text.disabled'}
        sx={{ fontStyle: value ? 'normal' : 'italic', fontSize: '0.91rem' }}
      >
        {value || (isAdmin ? 'คลิกเพื่อเพิ่ม' : '-')}
      </Typography>
    </Box>
  );
}

// ── Inline editable amount (currency, no max) ─────────────────────────────────

function InlineAmountCell({
  projectId,
  field,
  value,
  onSaved,
  isAdmin,
  alwaysRed = false,
}) {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();
  const cancelRef = useRef(false);
  const committedRef = useRef(false);

  const startEdit = () => {
    if (!isAdmin) return;
    cancelRef.current = false;
    committedRef.current = false;
    setDraft(String(value ?? 0));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const handleSave = async (val) => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    if (committedRef.current) return;
    committedRef.current = true;
    const num = Number(val);
    setSaving(true);
    try {
      await dispatch(
        actions.projectPatchDashboard(projectId, { [field]: num }),
      );
      onSaved(num);
    } catch (_) {
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing)
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        value={draft}
        type="number"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSave(draft);
          }
          if (e.key === 'Escape') {
            cancelRef.current = true;
            setEditing(false);
          }
        }}
        onBlur={(e) => handleSave(e.target.value)}
        sx={{
          width: 90,
          '& input': {
            py: 0.3,
            px: 0.5,
            fontSize: '0.96rem',
            textAlign: 'right',
          },
        }}
        inputProps={{ min: 0 }}
        disabled={saving}
      />
    );

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="flex-end"
      onClick={isAdmin ? startEdit : undefined}
      sx={{
        cursor: isAdmin ? 'pointer' : 'default',
        '&:hover .edit-icon': isAdmin ? { opacity: 1 } : {},
      }}
    >
      <Typography
        variant="caption"
        sx={{
          whiteSpace: 'nowrap',
          color: alwaysRed
            ? 'error.main'
            : Number(value) > 0
            ? 'text.primary'
            : 'text.disabled',
        }}
      >
        {fmt(value || 0)}
      </Typography>
      {isAdmin && (
        <EditIcon
          className="edit-icon"
          sx={{
            fontSize: 12,
            opacity: 0,
            color: 'text.disabled',
            transition: 'opacity 0.15s',
          }}
        />
      )}
    </Stack>
  );
}

// ── Total footer row ──────────────────────────────────────────────────────────

const TotalRow = React.memo(function TotalRow({
  rows,
  showPoDetail,
  stickyLeft,
  freezeCols,
  rowRef,
  inclBgRemain = true,
}) {
  const sum = (field) =>
    rows.reduce((acc, r) => acc + (Number(r[field]) || 0), 0);
  const totalPo = rows.reduce(
    (acc, r) =>
      acc + (r.include_vat ? (r.cost || 0) - (r.vat || 0) : r.cost || 0),
    0,
  );
  const totalReceived = rows.reduce(
    (acc, r) => acc + (r.received || 0) + (r.seed_received || 0),
    0,
  );
  const totalBilled = rows.reduce(
    (acc, r) => acc + (r.billed_waiting || 0) + (r.seed_billed_waiting || 0),
    0,
  );
  const fr = (col) =>
    stickyLeft && freezeCols
      ? { position: 'sticky', left: stickyLeft[col] ?? 0, zIndex: 2 }
      : {};

  return (
    <TableRow ref={rowRef}>
      <TableCell sx={TOT_SX} />
      <TableCell sx={{ ...TOT_SX, ...fr(1) }} colSpan={3} />
      <TableCell sx={{ ...TOT_SX, ...fr(4) }} />
      <TableCell sx={{ ...TOT_SX, ...fr(5) }} />
      <TableCell sx={{ ...TOT_SX, ...fr(6) }} align="right">
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', whiteSpace: 'nowrap', display: 'block' }}
        >
          {fmt(totalPo)}
        </Typography>
      </TableCell>
      {showPoDetail &&
        (() => {
          const totalPoRemaining = totalPo - totalReceived - totalBilled;
          return (
            <>
              <TableCell sx={TOT_GRP} align="right">
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {fmt(totalBilled)}
                </Typography>
              </TableCell>
              <TableCell sx={TOT_GRP} align="right">
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {fmt(totalReceived)}
                </Typography>
              </TableCell>
              <TableCell sx={TOT_GRP} align="right">
                <Typography
                  variant="caption"
                  sx={{
                    color: totalPoRemaining < 0 ? 'error.main' : 'text.primary',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {fmt(totalPoRemaining)}
                </Typography>
              </TableCell>
            </>
          );
        })()}
      {/* payment dates group col 27-30 + 31 */}
      <TableCell sx={TOT_GRP4} />
      <TableCell sx={TOT_GRP4} />
      <TableCell sx={TOT_GRP4} />
      <TableCell sx={TOT_GRP4} />
      <TableCell sx={TOT_GRP4} />
      {/* ทุนคาดการณ์ = BG(col33) - ใช้ไปแล้ว(col11) */}
      <TableCell sx={TOT_GRP2} align="right">
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', whiteSpace: 'nowrap', display: 'block' }}
        >
          {fmt(rows.reduce((acc, r) => acc + getProjectedFund(r), 0))}
        </Typography>
      </TableCell>
      {/* ใช้ไปแล้ว (Paid) */}
      <TableCell sx={TOT_GRP2} align="right">
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', whiteSpace: 'nowrap', display: 'block' }}
        >
          {fmt(rows.reduce((acc, r) => acc + getPaidValue(r), 0))}
        </Typography>
      </TableCell>
      {/* เจ้าหนี้ */}
      <TableCell sx={TOT_GRP2} align="right">
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', display: 'block' }}
        >
          {fmt(sum('seed_ap_pending'))}
        </Typography>
      </TableCell>
      {/* รอจ่าย (col35) */}
      <TableCell sx={TOT_GRP2} align="right">
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', display: 'block' }}
        >
          {fmt(rows.reduce((acc, r) => acc + getExpectedValue(r), 0))}
        </Typography>
      </TableCell>
      {/* AP รอจ่าย (col32) */}
      <TableCell sx={TOT_GRP2} align="right">
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', display: 'block' }}
        >
          {fmt(sum('ap_pending_unpaid'))}
        </Typography>
      </TableCell>
      {/* % BG */}
      <TableCell sx={TOT_GRP2} />
      {/* เกิน BG (col14) */}
      <TableCell sx={TOT_GRP2} align="right">
        {(() => {
          const totalCol14 = rows.reduce(
            (acc, r) =>
              acc +
              Math.min((r.total_budget ?? 0) - (r.total_expenses ?? 0), 0),
            0,
          );
          return (
            <Typography
              variant="caption"
              sx={{
                color: totalCol14 < 0 ? 'error.main' : 'text.disabled',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {totalCol14 < 0 ? fmt(totalCol14) : '—'}
            </Typography>
          );
        })()}
      </TableCell>
      {/* col33: BG (วงเงิน = total_budget) */}
      <TableCell sx={TOT_GRP3} align="right">
        <Typography
          variant="caption"
          sx={{ color: 'text.primary', whiteSpace: 'nowrap', display: 'block' }}
        >
          {fmt(sum('total_budget'))}
        </Typography>
      </TableCell>
      {/* col15-17 */}
      {(() => {
        const totalCol15 = rows.reduce(
          (acc, r) => acc + getBgColumnValues(r, inclBgRemain).col15,
          0,
        );
        const totalCol16 = rows.reduce(
          (acc, r) => acc + getBgColumnValues(r, inclBgRemain).col16,
          0,
        );
        const totalCol17 = rows.reduce(
          (acc, r) => acc + getBgColumnValues(r, inclBgRemain).col17,
          0,
        );
        const totalRefundPending = rows.reduce(
          (acc, r) => acc + getRefundPendingValue(r),
          0,
        );
        return (
          <>
            <TableCell sx={TOT_GRP3} align="right">
              <Typography
                variant="caption"
                sx={{
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {fmt(totalCol15)}
              </Typography>
            </TableCell>
            <TableCell sx={TOT_GRP3} align="right">
              <Typography
                variant="caption"
                sx={{
                  color: totalRefundPending > 0 ? 'error.main' : 'text.primary',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {totalRefundPending > 0 ? '-' : ''}
                {fmt(totalRefundPending)}
              </Typography>
            </TableCell>
            <TableCell sx={TOT_GRP3} align="right">
              <Typography
                variant="caption"
                sx={{
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {fmt(totalCol16)}
              </Typography>
            </TableCell>
            <TableCell sx={TOT_GRP3} align="right">
              <Typography
                variant="caption"
                sx={{
                  color: totalCol17 > 0 ? 'error.main' : 'text.primary',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {fmt(totalCol17)}
              </Typography>
            </TableCell>
          </>
        );
      })()}
      {/* กำไร col19 */}
      <TableCell sx={TOT_GRP3} align="right">
        {(() => {
          const totalRealprofit = rows.reduce(
            (acc, r) => acc + getEstimatedProfit(r),
            0,
          );
          return (
            <Typography
              variant="caption"
              sx={{
                color: totalRealprofit < 0 ? 'error.main' : 'success.main',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {fmt(totalRealprofit)}
            </Typography>
          );
        })()}
      </TableCell>
      {/* % กำไร */}
      <TableCell sx={TOT_GRP3} />
      {/* หมายเหตุ */}
      <TableCell sx={TOT_SX} />
    </TableRow>
  );
});

// ── Column defaults ───────────────────────────────────────────────────────────

const COL_DEFAULTS = {
  1: 68,
  2: 76,
  3: 44,
  4: 150,
  5: 130,
  6: 100,
  7: 96,
  8: 96,
  9: 96,
  27: 88,
  28: 70,
  31: 76,
  29: 88,
  30: 78,
  10: 100,
  33: 92,
  11: 92,
  12: 92,
  35: 92,
  32: 92,
  13: 70,
  14: 92,
  15: 92,
  34: 92,
  16: 90,
  17: 90,
  19: 108,
  20: 70,
  26: 110,
};

function ResizeHandle({ onStart }) {
  return (
    <Box
      component="span"
      onMouseDown={onStart}
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: 5,
        cursor: 'col-resize',
        zIndex: 10,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
      }}
    />
  );
}

// ── Seed value cell — full-cell click triggers edit mode ─────────────────────

function SeedCell({
  projectId,
  field,
  seedValue,
  autoValue,
  isAdmin,
  onSaved,
}) {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();
  const cancelRef = useRef(false);

  const combined = (Number(autoValue) || 0) + (Number(seedValue) || 0);

  const startEdit = (e) => {
    if (!isAdmin) return;
    e.stopPropagation();
    cancelRef.current = false;
    setDraft(String(seedValue ?? 0));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const handleBlur = async (e) => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    const num = Number(e.target.value);
    setSaving(true);
    try {
      await dispatch(
        actions.projectPatchDashboard(projectId, { [field]: num }),
      );
      onSaved(num);
    } catch (_) {
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing)
    return (
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '1rem',
            whiteSpace: 'nowrap',
            display: 'block',
            color: combined < 0 ? 'error.main' : 'text.primary',
          }}
        >
          {fmt(combined)}
        </Typography>
        <Box sx={{ mt: 0.3, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            size="small"
            value={draft}
            type="number"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                cancelRef.current = true;
                setEditing(false);
              }
            }}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: 80,
              '& input': {
                py: 0.3,
                px: 0.5,
                fontSize: '0.91rem',
                textAlign: 'right',
              },
            }}
            inputProps={{ min: 0 }}
            disabled={saving}
          />
          {saving && (
            <CircularProgress size={12} sx={{ ml: 0.5, alignSelf: 'center' }} />
          )}
        </Box>
      </Box>
    );

  return (
    <Box
      onClick={isAdmin ? startEdit : undefined}
      sx={{
        textAlign: 'right',
        cursor: isAdmin ? 'pointer' : 'default',
        borderRadius: 0.5,
        p: 0.3,
        '&:hover': isAdmin ? { bgcolor: 'action.hover' } : {},
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '1rem',
          whiteSpace: 'nowrap',
          display: 'block',
          color: combined < 0 ? 'error.main' : 'text.primary',
        }}
      >
        {fmt(combined)}
      </Typography>
      {isAdmin && (
        <Typography
          sx={{
            fontSize: '0.65rem',
            color: 'text.disabled',
            display: 'block',
            lineHeight: 1,
            opacity: 0.7,
          }}
        >
          {Number(seedValue) > 0
            ? `ตั้งต้น: ${fmt(seedValue)}`
            : 'คลิกเพื่อกำหนด'}
        </Typography>
      )}
    </Box>
  );
}

// ── Editable dash-status cell (cols 22-26) ───────────────────────────────────

// ── Job filter panel ──────────────────────────────────────────────────────────

// ── Perceived perf: debounce helper (React 17 compat) ────────────────────────
function useDebouncedValue(value, delay = 180) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Main component ────────────────────────────────────────────────────────────

const ALL_WORK_KEYS = ['', 'waiting', 'in_progress', 'done'];
const ALL_FINANCE_KEYS = [
  '',
  'none',
  'partial',
  'billed_waiting',
  'last_installment',
  'completed_po',
];
const DIM3_FILTER_DEFAULT = {
  work: Object.fromEntries(ALL_WORK_KEYS.map((k) => [k, true])),
  finance: Object.fromEntries(ALL_FINANCE_KEYS.map((k) => [k, true])),
  legal_warranty: true,
  legal_debt: true,
  legal_lawsuit: true,
};

export default function ProjectDashboardTable({
  projects,
  isLoading,
  isAdmin,
  onClickProject,
  onContactUpdated,
  onRowUpdated,
  searchTerm = '',
  statusFilter = '',
  onStatusFilterChange,
  jsFilter = true,
  onJsFilterChange,
  statusSummaryChips,
  showPoDetail = true,
  onShowPoDetailChange,
  pageScale = 1,
  excludeFilter = new Set(),
  onExcludeFilterChange,
  defaultExcludeFilter = new Set(),
  excludeCounts = {},
  customerFilter: customerFilterProp = '',
  onCustomerFilterChange,
  projectFilter = '',
  onProjectFilterChange,
  onTotalsChange,
  onLocalDataChange,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const containerRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const customerFilter = customerFilterProp;
  const setCustomerFilter = (v) =>
    onCustomerFilterChange?.(
      typeof v === 'function' ? v(customerFilterProp) : v,
    );
  const setProjectFilter = (v) => onProjectFilterChange?.(v);
  const clickTimerRef = useRef(null);
  const scheduleClick = (action) => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      action();
      clickTimerRef.current = null;
    }, 220);
  };
  const cancelAndRun = (action) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    action();
  };
  const [nameTooltip, setNameTooltip] = useState({
    open: false,
    name: '',
    x: 0,
    y: 0,
  });
  const showNameTooltip = (e, name) => {
    e.preventDefault();
    setNameTooltip({ open: true, name, x: e.clientX, y: e.clientY });
  };
  const hideNameTooltip = () =>
    setNameTooltip((p) => (p.open ? { ...p, open: false } : p));
  const setStatusFilter = (v) => onStatusFilterChange?.(v);
  const setShowPoDetail = (v) =>
    onShowPoDetailChange?.(typeof v === 'function' ? v(showPoDetail) : v);
  const [localData, setLocalData] = useState({});
  const [sortDir, setSortDir] = useState(null); // null=default group order, 'asc', 'desc'
  const [profitSort, setProfitSort] = useState(null); // null, 'asc', 'desc'
  const setJsFilter = (v) =>
    onJsFilterChange?.(typeof v === 'function' ? v(jsFilter) : v);
  const [colSort, setColSort] = useState({ key: null, dir: null });
  const [statusMenu, setStatusMenu] = useState(null); // { anchorEl, projectId, current }
  const [dim3Popover, setDim3Popover] = useState(null); // { anchorEl, projectId, work_status, finance_status, bg_usage_category, legal_* }
  const [seedModal, setSeedModal] = useState(null); // { projectId, projectName, field, label, seedValue, autoValue }
  const [seedDraft, setSeedDraft] = useState('');
  const [seedSaving, setSeedSaving] = useState(false);
  const seedInputRef = useRef();
  const [deliveryModal, setDeliveryModal] = useState(null); // { projectId, projectName, currentDate }
  const [deliveryDraft, setDeliveryDraft] = useState('');
  const [deliverySaving, setDeliverySaving] = useState(false);
  const [budgetListModal, setBudgetListModal] = useState(null); // { projectId, projectName, budgets }
  const [pendingBudget, setPendingBudget] = useState(null); // budget currently open in PendingItemsModal
  const [showTooltip, setShowTooltip] = useState(false);
  const [freezeCols, setFreezeCols] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showPaymentCols, setShowPaymentCols] = useState(true);
  const [inclBgRemain, setInclBgRemain] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const pageScaleRef = useRef(pageScale);
  useEffect(() => {
    pageScaleRef.current = pageScale;
  }, [pageScale]);

  const [colWidths, setColWidths] = useState({});

  const startResize = useCallback((colKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.currentTarget.closest('th');
    const startW = th ? th.offsetWidth : COL_DEFAULTS[colKey] || 80;
    const onMove = (me) => {
      setColWidths((prev) => ({
        ...prev,
        [colKey]: Math.max(40, startW + me.clientX - startX),
      }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const [contactModal, setContactModal] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const openContactModal = (contact) => {
    setContactModal(contact);
    setEditMode(false);
    setSaveError('');
  };
  const handleEditStart = () => {
    setEditForm({ ...contactModal });
    setEditMode(true);
    setSaveError('');
  };
  const handleEditCancel = () => {
    setEditMode(false);
    setSaveError('');
  };
  const handleFormChange = (field) => (e) =>
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await dispatch(actions.contactPut(contactModal._id, editForm));
      setContactModal((prev) => ({ ...prev, ...editForm }));
      setEditMode(false);
      onContactUpdated?.();
    } catch (err) {
      setSaveError(err?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => sortProjects(projects?.rows), [projects?.rows]);

  const getCustomerName = useCallback(
    (r) => r.client_id?.name || r.customer || '',
    [],
  );

  const filteredRows = useMemo(() => {
    let r = rows;
    if (projectFilter)
      r = r.filter((row) => row.project_number === projectFilter);
    if (customerFilter)
      r = r.filter((row) =>
        getCustomerName(row)
          .toLowerCase()
          .includes(customerFilter.toLowerCase()),
      );
    return r;
  }, [rows, customerFilter, projectFilter, getCustomerName]);

  const handleSaveLocal = useCallback((id, field, val) => {
    setLocalData((prev) => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  }, []);

  const handleStatusChange = async (newStatus) => {
    const { projectId, current } = statusMenu;
    setStatusMenu(null);

    const dash = STATUS_TO_DASH[newStatus] || {};
    const commitment = computeCommitment(
      dash.dash_work,
      dash.dash_delivery,
      dash.dash_disbursement,
      dash.dash_warranty,
      dash.dash_billing,
      dash.dash_payment,
    );
    const fullPayload = {
      operation_status: newStatus,
      ...dash,
      dash_commitment: commitment,
    };

    // Optimistic — อัปทันทีทั้ง local state และ Redux
    Object.entries(fullPayload).forEach(([k, v]) =>
      handleSaveLocal(projectId, k, v),
    );
    dispatch(actions.projectUpdateLocal(projectId, fullPayload));

    try {
      await dispatch(actions.projectPatchDashboard(projectId, fullPayload));
    } catch (err) {
      // Revert — คืนค่าเดิมทั้งหมด (undefined → getVal fallback ไปใช้ row field เดิม)
      Object.keys(fullPayload).forEach((k) =>
        handleSaveLocal(projectId, k, undefined),
      );
      handleSaveLocal(projectId, 'operation_status', current);
      dispatch(
        actions.projectUpdateLocal(projectId, { operation_status: current }),
      );
    }
  };

  const handleDim3Change = async (field, value) => {
    if (!dim3Popover) return;
    const { projectId } = dim3Popover;

    // Cascade rules — สร้าง patch ที่รวม auto-fill ทั้งหมด
    const patch = { [field]: value };

    // การเงิน = completed_po / last_installment → งานต้องเสร็จแล้ว
    if (
      field === 'finance_status' &&
      (value === 'completed_po' || value === 'last_installment')
    ) {
      patch.work_status = 'done';
    }

    // งาน = waiting / in_progress → Warranty ต้อง uncheck (ยังไม่ส่งงาน)
    if (
      field === 'work_status' &&
      (value === 'waiting' || value === 'in_progress')
    ) {
      patch.legal_warranty = false;
      patch.bg_usage_category = '';
    }

    // Warranty ติ๊ก → งานส่งแล้วแน่นอน → auto งาน = done
    if (field === 'legal_warranty' && value === true) {
      patch.work_status = 'done';
    }

    if (
      field === 'bg_usage_category' &&
      value === NO_BG_USAGE_KEY &&
      dim3Popover.work_status !== 'done'
    ) {
      return;
    }

    // ── col2 ↔ col3 sync ──────────────────────────────────────────────────────
    // work_status หลัง cascade → sync progress ให้ตรงกัน
    const effectiveWs =
      patch.work_status !== undefined
        ? patch.work_status
        : dim3Popover.work_status;
    const currentProgress =
      localData[dim3Popover.projectId]?.progress !== undefined
        ? localData[dim3Popover.projectId].progress
        : rows.find((r) => r._id === dim3Popover.projectId)?.progress ?? 0;
    if (effectiveWs === 'done' && Number(currentProgress) < 100) {
      patch.progress = 100;
    } else if (effectiveWs !== 'done' && Number(currentProgress) >= 100) {
      patch.progress = 0;
    }

    // อัป Popover state ทั้ง patch + track auto-filled fields
    const autoFilled = Object.keys(patch).filter((k) => k !== field);
    setDim3Popover((prev) => ({ ...prev, ...patch, _autofilled: autoFilled }));
    Object.entries(patch).forEach(([k, v]) => handleSaveLocal(projectId, k, v));
    dispatch(actions.projectUpdateLocal(projectId, patch));
    try {
      await dispatch(actions.projectPatchDashboard(projectId, patch));
      onRowUpdated?.(projectId, patch);
    } catch (err) {
      Object.keys(patch).forEach((k) =>
        handleSaveLocal(projectId, k, undefined),
      );
      dispatch(
        actions.projectUpdateLocal(
          projectId,
          Object.fromEntries(Object.keys(patch).map((k) => [k, undefined])),
        ),
      );
    }
  };

  const SEED_LABELS = {
    seed_received: 'รับเงินมาแล้ว',
    seed_billed_waiting: 'วางบิลแล้วรอรับเงิน',
    seed_ap_pending: 'เจ้าหนี้รอชำระ',
  };

  const openDeliveryModal = (projectId, projectName, currentDate) => {
    if (!isAdmin) return;
    setDeliveryDraft(toDateInput(currentDate));
    setDeliveryModal({ projectId, projectName, currentDate });
  };

  const saveDeliveryModal = async () => {
    if (!deliveryModal) return;
    const { projectId } = deliveryModal;
    setDeliverySaving(true);
    try {
      const value = deliveryDraft || null;
      await dispatch(
        actions.projectPatchDashboard(projectId, { delivery_date: value }),
      );
      handleSaveLocal(projectId, 'delivery_date', value);
      onRowUpdated?.(projectId, { delivery_date: value });
      setDeliveryModal(null);
    } catch (_) {
    } finally {
      setDeliverySaving(false);
    }
  };

  const openBudgetListModal = (row) => {
    setBudgetListModal({
      projectId: row._id,
      projectNumber: row.project_number,
      projectName: row.name,
      budgets: row.budget || [],
    });
  };

  const closeBudgetListModal = () => setBudgetListModal(null);

  const openPendingBudgetModal = (b) => setPendingBudget(b);
  const closePendingBudgetModal = () => setPendingBudget(null);

  const handleBudgetPendingUpdated = (updatedBudget) => {
    if (!budgetListModal) return;
    const updatedBudgetId = updatedBudget?.id || updatedBudget?._id;
    const nextBudgets = budgetListModal.budgets.map((b) =>
      (b.id || b._id) === updatedBudgetId ? updatedBudget : b,
    );
    setBudgetListModal((prev) =>
      prev ? { ...prev, budgets: nextBudgets } : prev,
    );
    const nextTotal = nextBudgets.reduce((acc, b) => {
      const unpaid = (b.pending_items || []).filter((i) => !i.paid);
      return acc + unpaid.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    }, 0);
    onRowUpdated?.(budgetListModal.projectId, {
      ap_pending_unpaid: nextTotal,
      budget: nextBudgets,
    });
  };

  const openSeedModal = (
    projectId,
    projectName,
    field,
    seedValue,
    autoValue,
  ) => {
    if (!isAdmin) return;
    setSeedDraft(String(seedValue ?? 0));
    setSeedModal({
      projectId,
      projectName,
      field,
      label: SEED_LABELS[field] || field,
      seedValue,
      autoValue,
    });
    setTimeout(() => seedInputRef.current?.select(), 80);
  };

  const saveSeedModal = async () => {
    if (!seedModal) return;
    const { projectId, field } = seedModal;
    setSeedSaving(true);
    try {
      const nextValue = Number(seedDraft);
      await dispatch(
        actions.projectPatchDashboard(projectId, { [field]: nextValue }),
      );
      handleSaveLocal(projectId, field, nextValue);
      onRowUpdated?.(projectId, { [field]: nextValue });
      setSeedModal(null);
    } catch (_) {
    } finally {
      setSeedSaving(false);
    }
  };

  const renderSeedCell = ({
    projectId,
    projectName,
    field,
    seedValue,
    autoValue,
    alwaysRed = false,
  }) => {
    const combined = (Number(autoValue) || 0) + (Number(seedValue) || 0);
    return (
      <TableCell
        key={field}
        sx={{
          ...TD_SX,
          textAlign: 'right',
          cursor: isAdmin ? 'pointer' : 'default',
          userSelect: 'none',
          '&:hover': isAdmin ? { bgcolor: 'rgba(99,102,241,0.06)' } : {},
        }}
        onClick={
          isAdmin
            ? (e) => {
                e.stopPropagation();
                openSeedModal(
                  projectId,
                  projectName,
                  field,
                  seedValue,
                  autoValue,
                );
              }
            : undefined
        }
      >
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 400,
              whiteSpace: 'nowrap',
              display: 'block',
              color: alwaysRed || combined < 0 ? 'error.main' : 'text.primary',
            }}
          >
            {fmt(combined)}
          </Typography>
          {isAdmin && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.76rem',
                color: 'text.disabled',
                display: 'block',
                lineHeight: 1,
              }}
            >
              {Number(seedValue) > 0
                ? `ตั้งต้น: ${fmt(seedValue)}`
                : 'คลิกเพื่อกำหนด'}
            </Typography>
          )}
        </Box>
      </TableCell>
    );
  };

  const getVal = (row, field) =>
    localData[row._id]?.[field] !== undefined
      ? localData[row._id][field]
      : row[field];

  const customers = useMemo(() => {
    return [
      ...new Set(rows.map((r) => getCustomerName(r)).filter(Boolean)),
    ].sort();
  }, [rows, getCustomerName]);

  // Compute minimum column widths from actual data (every row + footer totals) so nothing overflows.
  // Digit ~8.2px, separator (,/./−) ~4px at 0.91rem Roboto + 16px cell padding.
  const effectiveColWidths = useMemo(() => {
    const numW = (n) => {
      const s = fmt(Number(n) || 0);
      if (s === '-') return 0;
      const digits = (s.match(/\d/g) || []).length;
      const others = s.length - digits;
      return Math.ceil(digits * 10 + others * 5.5) + 32;
    };
    const maxW = (vals) => vals.reduce((mx, v) => Math.max(mx, numW(v)), 0);

    if (!rows.length) return { ...COL_DEFAULTS };

    const getPo = (r) =>
      r.include_vat ? (r.cost || 0) - (r.vat || 0) : r.cost || 0;
    const poVals = rows.map(getPo);

    const rowMax = {
      6: maxW(poVals),
      7: maxW(
        rows.map((r) => (r.billed_waiting || 0) + (r.seed_billed_waiting || 0)),
      ),
      8: maxW(rows.map((r) => (r.received || 0) + (r.seed_received || 0))),
      9: maxW(
        rows.map(
          (r, i) =>
            poVals[i] -
            (r.received || 0) -
            (r.seed_received || 0) -
            (r.billed_waiting || 0) -
            (r.seed_billed_waiting || 0),
        ),
      ),
      10: maxW(rows.map((r) => getProjectedFund(r))),
      33: maxW(rows.map((r) => r.total_budget || 0)),
      11: maxW(rows.map((r) => r.total_expenses || 0)),
      12: maxW(rows.map((r) => r.seed_ap_pending || 0)),
      35: maxW(rows.map((r) => getExpectedValue(r))),
      32: maxW(rows.map((r) => r.ap_pending_unpaid || 0)),
      14: maxW(
        rows.map((r) => (r.total_budget || 0) - (r.total_expenses || 0)),
      ),
      15: maxW(rows.map((r) => getBgColumnValues(r, inclBgRemain).col15)),
      34: maxW(rows.map((r) => getRefundPendingValue(r))),
      16: maxW(rows.map((r) => r.to_spend || 0)),
      17: maxW(rows.map((r) => getBgColumnValues(r, inclBgRemain).col17)),
      19: maxW(
        rows.map(
          (r) =>
            getPo(r) -
            (r.total_expenses || 0) -
            getBgColumnValues(r, inclBgRemain).col17,
        ),
      ),
    };

    const totPo = poVals.reduce((a, v) => a + v, 0);
    const totBilled = rows.reduce(
      (a, r) => a + (r.billed_waiting || 0) + (r.seed_billed_waiting || 0),
      0,
    );
    const totReceived = rows.reduce(
      (a, r) => a + (r.received || 0) + (r.seed_received || 0),
      0,
    );
    const totBudget = rows.reduce((a, r) => a + (r.total_budget || 0), 0);
    const totProjectedFund = rows.reduce(
      (a, r) => a + getProjectedFund(r),
      0,
    );
    const totSpent = rows.reduce((a, r) => a + getPaidValue(r), 0);
    const totAp = rows.reduce((a, r) => a + (r.seed_ap_pending || 0), 0);
    const totExpected = rows.reduce((a, r) => a + getExpectedValue(r), 0);
    const totApPendingUnpaid = rows.reduce(
      (a, r) => a + (r.ap_pending_unpaid || 0),
      0,
    );
    const totCol14 = rows.reduce(
      (a, r) =>
        a + Math.min((r.total_budget || 0) - (r.total_expenses || 0), 0),
      0,
    );
    const totCol15 = rows.reduce(
      (a, r) => a + getBgColumnValues(r, inclBgRemain).col15,
      0,
    );
    const totCol16 = rows.reduce(
      (a, r) => a + getBgColumnValues(r, inclBgRemain).col16,
      0,
    );
    const totCol17 = rows.reduce(
      (a, r) => a + getBgColumnValues(r, inclBgRemain).col17,
      0,
    );
    const totRefundPending = rows.reduce(
      (a, r) => a + getRefundPendingValue(r),
      0,
    );
    const totProfit = rows.reduce((a, r) => a + getEstimatedProfit(r), 0);

    const totMax = {
      6: numW(totPo),
      7: numW(totBilled),
      8: numW(totReceived),
      9: numW(totPo - totReceived - totBilled),
      10: numW(totProjectedFund),
      33: numW(totBudget),
      11: numW(totSpent),
      12: numW(totAp),
      35: numW(totExpected),
      32: numW(totApPendingUnpaid),
      14: numW(totCol14),
      15: numW(totCol15),
      34: numW(totRefundPending),
      16: numW(totCol16),
      17: numW(totCol17),
      19: numW(totProfit),
    };

    const result = { ...COL_DEFAULTS };
    [6, 7, 8, 9, 10, 33, 11, 12, 35, 32, 14, 15, 34, 16, 17, 19].forEach((col) => {
      result[col] = Math.max(
        COL_DEFAULTS[col] || 0,
        rowMax[col] || 0,
        totMax[col] || 0,
      );
    });
    return result;
  }, [rows, inclBgRemain]);

  // getW: user resize (colWidths) can only go WIDER than content-required width, never narrower
  const getW = useCallback(
    (col) =>
      Math.max(
        colWidths[col] || 0,
        effectiveColWidths[col] || COL_DEFAULTS[col] || 80,
      ),
    [colWidths, effectiveColWidths],
  );

  const stickyLeftOf = useMemo(() => {
    const result = {};
    let offset = 0;
    for (let i = 1; i <= 6; i++) {
      result[i] = offset;
      offset += getW(i);
    }
    return result;
  }, [getW]);

  const frzTh = (col) =>
    freezeCols
      ? { position: 'sticky', left: stickyLeftOf[col], zIndex: 4 }
      : {};

  const onContainerMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (
      e.target.closest(
        'button, input, a, [role="button"], [class*="MuiIconButton"]',
      )
    )
      return;
    const el = containerRef.current;
    if (!el) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = el.scrollLeft;
    const startTop = el.scrollTop;
    let moved = false;
    const onMove = (me) => {
      const dx = me.clientX - startX;
      const dy = me.clientY - startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
      if (moved) {
        el.scrollLeft = startLeft - dx / pageScaleRef.current;
        el.scrollTop = startTop - dy / pageScaleRef.current;
        me.preventDefault();
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setIsDragging(false);
    };
    setIsDragging(true);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const cycleSort = () => {
    setSortDir((prev) =>
      prev === null ? 'desc' : prev === 'desc' ? 'asc' : null,
    );
  };

  const cycleProfitSort = () => {
    setProfitSort((prev) =>
      prev === null ? 'desc' : prev === 'desc' ? 'asc' : null,
    );
  };

  const cycleColSort = (key) => {
    setColSort((prev) => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: null, dir: null };
    });
  };

  const sortIcon = (key) =>
    colSort.key === key ? (
      colSort.dir === 'asc' ? (
        <ArrowDropUpIcon sx={{ fontSize: 40, color: '#ef4444' }} />
      ) : (
        <ArrowDropDownIcon sx={{ fontSize: 40, color: '#ef4444' }} />
      )
    ) : (
      <SwapVertIcon sx={{ fontSize: 13, opacity: 0.55 }} />
    );

  const thSortSx = (key, base, hoverBg = '#7dd3fc') => ({
    ...base,
    cursor: 'pointer',
    userSelect: 'none',
    ...(colSort.key === key && {
      bgcolor: 'rgba(192,150,20,0.92)',
      color: '#fff',
      borderBottom: '3px solid #d4a017',
    }),
    '&:hover': {
      bgcolor: colSort.key === key ? 'rgba(155,120,15,0.95)' : hoverBg,
    },
  });

  // ── Perceived perf: debounce searchTerm so typing stays responsive ──────────
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 180);

  // ── Actual perf: filter+sort only recalculates when inputs truly change ──────
  const displayRows = useMemo(() => {
    let result = filteredRows;

    if (debouncedSearchTerm) {
      const q = debouncedSearchTerm.toLowerCase();
      result = result.filter((r) => {
        return (
          (r.project_number || '').toLowerCase().includes(q) ||
          (r.name || '').toLowerCase().includes(q) ||
          (r.client_id?.name || '').toLowerCase().includes(q) ||
          String(r.cost || '').includes(q) ||
          (localData[r._id]?.dashboard_note ?? r.dashboard_note ?? '')
            .toString()
            .toLowerCase()
            .includes(q)
        );
      });
    }

    if (jsFilter) {
      result = result.filter((r) => /^JS_/i.test(r.project_number));
    }

    if (statusFilter) {
      result = result.filter((r) => {
        const ov = localData[r._id] || {};
        const ws =
          (ov.work_status !== undefined ? ov.work_status : r.work_status) || '';
        const fs =
          (ov.finance_status !== undefined
            ? ov.finance_status
            : r.finance_status) || '';
        const bgUsage =
          (ov.bg_usage_category !== undefined
            ? ov.bg_usage_category
            : r.bg_usage_category) || '';
        const lw = Boolean(
          ov.legal_warranty !== undefined
            ? ov.legal_warranty
            : r.legal_warranty,
        );
        const ld = Boolean(
          ov.legal_debt !== undefined ? ov.legal_debt : r.legal_debt,
        );
        const ll = Boolean(
          ov.legal_lawsuit !== undefined ? ov.legal_lawsuit : r.legal_lawsuit,
        );
        const hasCom = ws !== 'done' || fs !== 'completed_po' || lw || ld || ll;
        if (statusFilter === 'has_commitment') return hasCom;
        if (statusFilter === 'no_commitment') return !hasCom;
        if (statusFilter === 'work_waiting') return !ws || ws === 'waiting';
        if (statusFilter === 'work_in_progress') return ws === 'in_progress';
        if (statusFilter === 'work_done') return ws === 'done';
        if (statusFilter === 'finance_none') return !fs || fs === 'none';
        if (statusFilter === 'finance_partial') return fs === 'partial';
        if (statusFilter === 'finance_billed_waiting')
          return fs === 'billed_waiting';
        if (statusFilter === 'finance_last_installment')
          return fs === 'last_installment';
        if (statusFilter === 'finance_completed_po')
          return fs === 'completed_po';
        if (statusFilter === 'bg_no_bg_usage')
          return bgUsage === NO_BG_USAGE_KEY;
        if (statusFilter === 'legal_warranty') return lw;
        if (statusFilter === 'legal_debt') return ld;
        if (statusFilter === 'legal_lawsuit') return ll;
        if (statusFilter === 'col9_positive') {
          const merged = { ...r, ...ov };
          const isJs = /^JS_/i.test(merged.project_number);
          const mws = merged.work_status || '';
          const mfs = merged.finance_status || '';
          const hasCom =
            mws !== 'done' ||
            mfs !== 'completed_po' ||
            merged.legal_warranty ||
            merged.legal_debt ||
            merged.legal_lawsuit;
          const po = merged.include_vat
            ? (merged.cost || 0) - (merged.vat || 0)
            : merged.cost || 0;
          const col9 =
            po -
            (merged.received || 0) -
            (merged.seed_received || 0) -
            (merged.billed_waiting || 0) -
            (merged.seed_billed_waiting || 0);
          return isJs && hasCom && Math.round(col9 * 100) > 0;
        }
        if (statusFilter === 'col7_positive') {
          const merged = { ...r, ...ov };
          const isJs = /^JS_/i.test(merged.project_number);
          const mws = merged.work_status || '';
          const mfs = merged.finance_status || '';
          const hasCom =
            mws !== 'done' ||
            mfs !== 'completed_po' ||
            merged.legal_warranty ||
            merged.legal_debt ||
            merged.legal_lawsuit;
          const col7 =
            (merged.billed_waiting || 0) + (merged.seed_billed_waiting || 0);
          return isJs && hasCom && Math.round(col7 * 100) > 0;
        }
        return true;
      });
    }

    if (excludeFilter.size > 0) {
      const excludeKeys = [...excludeFilter];
      result = result.filter((r) => {
        const ov = localData[r._id] || {};
        const ws =
          (ov.work_status !== undefined ? ov.work_status : r.work_status) || '';
        const fs =
          (ov.finance_status !== undefined
            ? ov.finance_status
            : r.finance_status) || '';
        const bgUsage =
          (ov.bg_usage_category !== undefined
            ? ov.bg_usage_category
            : r.bg_usage_category) || '';
        const lw = Boolean(
          ov.legal_warranty !== undefined
            ? ov.legal_warranty
            : r.legal_warranty,
        );
        const ld = Boolean(
          ov.legal_debt !== undefined ? ov.legal_debt : r.legal_debt,
        );
        const ll = Boolean(
          ov.legal_lawsuit !== undefined ? ov.legal_lawsuit : r.legal_lawsuit,
        );
        const hasCom = ws !== 'done' || fs !== 'completed_po' || lw || ld || ll;
        const matchKey = (key) => {
          if (key === 'has_commitment') return hasCom;
          if (key === 'no_commitment') return !hasCom;
          if (key === 'work_waiting') return !ws || ws === 'waiting';
          if (key === 'work_in_progress') return ws === 'in_progress';
          if (key === 'work_done') return ws === 'done';
          if (key === 'finance_none') return !fs || fs === 'none';
          if (key === 'finance_partial') return fs === 'partial';
          if (key === 'finance_billed_waiting') return fs === 'billed_waiting';
          if (key === 'finance_last_installment')
            return fs === 'last_installment';
          if (key === 'finance_completed_po') return fs === 'completed_po';
          if (key === 'bg_no_bg_usage') return bgUsage === NO_BG_USAGE_KEY;
          if (key === 'legal_warranty') return lw;
          if (key === 'legal_debt') return ld;
          if (key === 'legal_lawsuit') return ll;
          if (key === 'col9_positive') {
            const merged = { ...r, ...ov };
            const po = merged.include_vat
              ? (merged.cost || 0) - (merged.vat || 0)
              : merged.cost || 0;
            const col9 =
              po -
              (merged.received || 0) -
              (merged.seed_received || 0) -
              (merged.billed_waiting || 0) -
              (merged.seed_billed_waiting || 0);
            return Math.round(col9 * 100) > 0;
          }
          if (key === 'col7_positive') {
            const merged = { ...r, ...ov };
            return (
              Math.round(
                ((merged.billed_waiting || 0) +
                  (merged.seed_billed_waiting || 0)) *
                  100,
              ) > 0
            );
          }
          return false;
        };
        return !excludeKeys.some(matchKey);
      });
    }

    if (sortDir !== null) {
      result = [...result].sort((a, b) => {
        const cmp = (a.project_number || '').localeCompare(
          b.project_number || '',
          'th',
          { numeric: true },
        );
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    if (profitSort !== null) {
      result = [...result].sort((a, b) => {
        const diff = (a.realprofit || 0) - (b.realprofit || 0);
        return profitSort === 'asc' ? diff : -diff;
      });
    }

    if (colSort.key) {
      result = [...result].sort((a, b) => {
        let valA;
        let valB;
        if (colSort.key === 'customer') {
          const cmp = getCustomerName(a).localeCompare(
            getCustomerName(b),
            'th',
          );
          return colSort.dir === 'asc' ? cmp : -cmp;
        }
        if (colSort.key === 'name') {
          const cmp = (a.name || '').localeCompare(b.name || '', 'th');
          return colSort.dir === 'asc' ? cmp : -cmp;
        }
        if (colSort.key === 'po') {
          valA = a.include_vat ? (a.cost || 0) - (a.vat || 0) : a.cost || 0;
          valB = b.include_vat ? (b.cost || 0) - (b.vat || 0) : b.cost || 0;
        } else if (colSort.key === 'received') {
          valA = (a.received ?? 0) + (a.seed_received ?? 0);
          valB = (b.received ?? 0) + (b.seed_received ?? 0);
        } else if (colSort.key === 'billed_waiting') {
          valA = (a.billed_waiting ?? 0) + (a.seed_billed_waiting ?? 0);
          valB = (b.billed_waiting ?? 0) + (b.seed_billed_waiting ?? 0);
        } else if (colSort.key === 'po_remaining') {
          const poA = a.include_vat
            ? (a.cost || 0) - (a.vat || 0)
            : a.cost || 0;
          const poB = b.include_vat
            ? (b.cost || 0) - (b.vat || 0)
            : b.cost || 0;
          valA =
            poA -
            (a.received ?? 0) -
            (a.seed_received ?? 0) -
            (a.billed_waiting ?? 0) -
            (a.seed_billed_waiting ?? 0);
          valB =
            poB -
            (b.received ?? 0) -
            (b.seed_received ?? 0) -
            (b.billed_waiting ?? 0) -
            (b.seed_billed_waiting ?? 0);
        } else if (colSort.key === 'progress') {
          const ldA = localData[a._id] || {};
          const ldB = localData[b._id] || {};
          valA =
            Number(ldA.progress !== undefined ? ldA.progress : a.progress) || 0;
          valB =
            Number(ldB.progress !== undefined ? ldB.progress : b.progress) || 0;
        } else if (colSort.key === 'total_budget') {
          valA = a.total_budget ?? 0;
          valB = b.total_budget ?? 0;
        } else if (colSort.key === 'projected_fund') {
          valA = getProjectedFund(a);
          valB = getProjectedFund(b);
        } else if (colSort.key === 'total_expenses') {
          valA = getPaidValue(a);
          valB = getPaidValue(b);
        } else if (colSort.key === 'seed_ap_pending') {
          const ldA = localData[a._id] || {};
          const ldB = localData[b._id] || {};
          valA =
            (ldA.seed_ap_pending !== undefined
              ? ldA.seed_ap_pending
              : a.seed_ap_pending) ?? 0;
          valB =
            (ldB.seed_ap_pending !== undefined
              ? ldB.seed_ap_pending
              : b.seed_ap_pending) ?? 0;
        } else if (colSort.key === 'expected_value') {
          valA = getExpectedValue(a);
          valB = getExpectedValue(b);
        } else if (colSort.key === 'ap_pending_unpaid') {
          valA = a.ap_pending_unpaid ?? 0;
          valB = b.ap_pending_unpaid ?? 0;
        } else if (colSort.key === 'bg_used_pct') {
          valA = a.bg_used_pct ?? 0;
          valB = b.bg_used_pct ?? 0;
        } else if (colSort.key === 'over_budget') {
          valA = (a.total_budget ?? 0) - (a.total_expenses ?? 0);
          valB = (b.total_budget ?? 0) - (b.total_expenses ?? 0);
        } else if (colSort.key === 'net_remaining') {
          valA = getBgColumnValues(a, inclBgRemain).col15;
          valB = getBgColumnValues(b, inclBgRemain).col15;
        } else if (colSort.key === 'refund_pending') {
          valA = getRefundPendingValue(a);
          valB = getRefundPendingValue(b);
        } else if (colSort.key === 'to_spend') {
          const ldA = localData[a._id] || {};
          const ldB = localData[b._id] || {};
          valA = (ldA.to_spend !== undefined ? ldA.to_spend : a.to_spend) ?? 0;
          valB = (ldB.to_spend !== undefined ? ldB.to_spend : b.to_spend) ?? 0;
        } else if (colSort.key === 'col17') {
          const mA = { ...a, ...(localData[a._id] || {}) };
          const mB = { ...b, ...(localData[b._id] || {}) };
          valA = getBgColumnValues(mA, inclBgRemain).col17;
          valB = getBgColumnValues(mB, inclBgRemain).col17;
        } else if (colSort.key === 'profit_pct') {
          const mA = { ...a, ...(localData[a._id] || {}) };
          const mB = { ...b, ...(localData[b._id] || {}) };
          const poA = a.include_vat
            ? (a.cost || 0) - (a.vat || 0)
            : a.cost || 0;
          const poB = b.include_vat
            ? (b.cost || 0) - (b.vat || 0)
            : b.cost || 0;
          const c17A = getBgColumnValues(mA, inclBgRemain).col17;
          const c17B = getBgColumnValues(mB, inclBgRemain).col17;
          valA =
            poA > 0 ? ((poA - (mA.total_expenses ?? 0) - c17A) / poA) * 100 : 0;
          valB =
            poB > 0 ? ((poB - (mB.total_expenses ?? 0) - c17B) / poB) * 100 : 0;
        }
        const diff = valA - valB;
        return colSort.dir === 'asc' ? diff : -diff;
      });
    }

    return result;
  }, [
    filteredRows,
    debouncedSearchTerm,
    jsFilter,
    statusFilter,
    sortDir,
    profitSort,
    colSort,
    localData,
    getCustomerName,
    excludeFilter,
    inclBgRemain,
  ]);

  const isStale = debouncedSearchTerm !== searchTerm;

  const mergedDisplayRows = useMemo(() => {
    return displayRows.map((row) => ({
      ...row,
      ...(localData[row._id] || {}),
    }));
  }, [displayRows, localData]);

  const totals = useMemo(() => {
    const rows = mergedDisplayRows;
    const sum = (f) => rows.reduce((acc, r) => acc + (Number(r[f]) || 0), 0);
    const totalPo = rows.reduce(
      (acc, r) =>
        acc + (r.include_vat ? (r.cost || 0) - (r.vat || 0) : r.cost || 0),
      0,
    );
    const totalReceived = rows.reduce(
      (acc, r) => acc + (r.received || 0) + (r.seed_received || 0),
      0,
    );
    const totalBilled = rows.reduce(
      (acc, r) => acc + (r.billed_waiting || 0) + (r.seed_billed_waiting || 0),
      0,
    );
    const col14 = rows.reduce(
      (acc, r) =>
        acc + Math.min((r.total_budget ?? 0) - (r.total_expenses ?? 0), 0),
      0,
    );
    const col15 = rows.reduce(
      (acc, r) => acc + getBgColumnValues(r, inclBgRemain).col15,
      0,
    );
    const col16 = rows.reduce(
      (acc, r) => acc + getBgColumnValues(r, inclBgRemain).col16,
      0,
    );
    const col17 = rows.reduce(
      (acc, r) => acc + getBgColumnValues(r, inclBgRemain).col17,
      0,
    );
    const profit = rows.reduce((acc, r) => acc + getEstimatedProfit(r), 0);
    return {
      po: totalPo,
      billed: totalBilled,
      received: totalReceived,
      poRemain: totalPo - totalReceived - totalBilled,
      budget: sum('total_budget'),
      projectedFund: rows.reduce((acc, r) => acc + getProjectedFund(r), 0),
      spent: rows.reduce((acc, r) => acc + getPaidValue(r), 0),
      apPending: sum('seed_ap_pending'),
      expected: rows.reduce((acc, r) => acc + getExpectedValue(r), 0),
      apPendingUnpaid: sum('ap_pending_unpaid'),
      col14,
      col15,
      refundPending: rows.reduce((acc, r) => acc + getRefundPendingValue(r), 0),
      col16,
      col17,
      profit,
    };
  }, [mergedDisplayRows, inclBgRemain]);

  useEffect(() => {
    onTotalsChange?.(totals);
  }, [totals]);

  useEffect(() => {
    onLocalDataChange?.(localData);
  }, [localData]);

  const TH_TOT = {
    position: 'absolute',
    bottom: 3,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#082f49',
    lineHeight: 1,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
  };

  return (
    <Box>
      {/* Filter bar */}
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          mb: 1.5,
          bgcolor: '#f0f9ff',
          border: '1px solid rgba(14,116,144,0.12)',
          borderRadius: '10px',
          px: 1.5,
          py: 0.9,
          gap: 0.75,
        }}
      >
        <Button
          size="small"
          variant={filterOpen ? 'contained' : 'outlined'}
          startIcon={<TuneIcon sx={{ fontSize: '0.9rem !important' }} />}
          onClick={() => setFilterOpen(true)}
          sx={{
            fontSize: '0.9rem',
            py: 0.35,
            px: 1.4,
            whiteSpace: 'nowrap',
            borderRadius: '6px',
            fontWeight: 600,
          }}
        >
          ตัวกรอง
        </Button>
        {(() => {
          const isDefault =
            statusFilter === 'has_commitment' &&
            jsFilter &&
            !customerFilter &&
            excludeFilter.size === defaultExcludeFilter.size &&
            [...defaultExcludeFilter].every((k) => excludeFilter.has(k));
          return (
            <Chip
              label="↺ Default"
              onClick={() => {
                setStatusFilter('has_commitment');
                setJsFilter(true);
                onExcludeFilterChange?.(new Set(defaultExcludeFilter));
                setCustomerFilter('');
              }}
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: '6px',
                '& .MuiChip-label': { px: 0.8, fontSize: '0.79rem' },
                ...(isDefault
                  ? {
                      bgcolor: 'transparent',
                      color: 'text.disabled',
                      border: '1px solid #e2e8f0',
                    }
                  : {
                      bgcolor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #fcd34d',
                      '&:hover': { bgcolor: '#fde68a' },
                    }),
              }}
            />
          );
        })()}
        {isLoading && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CircularProgress size={12} />
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.84rem' }}
            >
              กำลังโหลด {projects?.rows?.length || 0} รายการ...
            </Typography>
          </Stack>
        )}
        {jsFilter && (
          <Chip
            label="JS_ เท่านั้น"
            onDelete={() => setJsFilter(false)}
            size="small"
            color="warning"
            sx={{
              height: 22,
              fontWeight: 600,
              borderRadius: '6px',
              '& .MuiChip-label': { px: 0.8, fontSize: '0.79rem' },
              '& .MuiChip-deleteIcon': { fontSize: 14, mr: 0.25 },
            }}
          />
        )}
        {customerFilter && (
          <Chip
            label={`ผู้ว่าจ้าง: ${customerFilter}`}
            onDelete={() => setCustomerFilter('')}
            size="small"
            color="primary"
            sx={{
              height: 22,
              borderRadius: '6px',
              '& .MuiChip-label': { px: 0.8, fontSize: '0.79rem' },
              '& .MuiChip-deleteIcon': { fontSize: 14, mr: 0.25 },
            }}
          />
        )}
        {statusFilter &&
          (() => {
            const meta =
              FILTER_META[statusFilter] || STATUS_MAP[statusFilter] || {};
            const label = meta.label || statusFilter;
            const color = meta.color || '#64748b';
            return (
              <Chip
                label={`${label} · ${displayRows.length}`}
                onDelete={() => setStatusFilter('')}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  borderRadius: '6px',
                  bgcolor: color,
                  color: '#fff',
                  border: `1px solid ${color}`,
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.79rem' },
                  '& .MuiChip-deleteIcon': {
                    fontSize: 14,
                    mr: 0.25,
                    color: '#fff',
                  },
                }}
              />
            );
          })()}
        {[...excludeFilter].map((key) => {
          const meta = FILTER_META[key] || {};
          return (
            <Chip
              key={key}
              label={`−${meta.label || key}${
                excludeCounts[key] != null ? ` · ${excludeCounts[key]}` : ''
              }`}
              onDelete={() =>
                onExcludeFilterChange?.((prev) => {
                  const s = new Set(prev);
                  s.delete(key);
                  return s;
                })
              }
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                borderRadius: '6px',
                bgcolor: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
                '& .MuiChip-label': { px: 0.8, fontSize: '0.79rem' },
                '& .MuiChip-deleteIcon': {
                  fontSize: 14,
                  mr: 0.25,
                  color: '#b91c1c',
                  '&:hover': { color: '#991b1b' },
                },
              }}
            />
          );
        })}
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={freezeCols}
              onChange={(e) => setFreezeCols(e.target.checked)}
              sx={{ p: 0.4 }}
            />
          }
          label={
            <Typography
              variant="caption"
              sx={{
                userSelect: 'none',
                fontSize: '0.84rem',
                color: 'text.secondary',
              }}
            >
              ล็อกคอลัมน์ 1–6
            </Typography>
          }
          sx={{ ml: 0, mr: 0 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={inclBgRemain}
              onChange={(e) => setInclBgRemain(e.target.checked)}
              sx={{ p: 0.4 }}
            />
          }
          label={
            <Typography
              variant="caption"
              sx={{
                userSelect: 'none',
                fontSize: '0.84rem',
                color: 'text.secondary',
              }}
            >
              รวม BG คงเหลือ
            </Typography>
          }
          sx={{ ml: 0, mr: 0 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showTooltip}
              onChange={(e) => setShowTooltip(e.target.checked)}
              sx={{ p: 0.4 }}
            />
          }
          label={
            <Typography
              variant="caption"
              sx={{
                userSelect: 'none',
                fontSize: '0.84rem',
                color: 'text.secondary',
              }}
            >
              tooltip
            </Typography>
          }
          sx={{ ml: 0, mr: 0.5 }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowPoDetail((v) => !v)}
          sx={{
            fontSize: '0.9rem',
            py: 0.35,
            px: 1.4,
            whiteSpace: 'nowrap',
            borderRadius: '6px',
            fontWeight: 600,
          }}
        >
          {showPoDetail ? '− ซ่อน คอล 7–9' : '+ รับเงิน · วางบิล · ยอดค้าง'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowPaymentCols((v) => !v)}
          sx={{
            fontSize: '0.9rem',
            py: 0.35,
            px: 1.4,
            whiteSpace: 'nowrap',
            borderRadius: '6px',
            fontWeight: 600,
          }}
        >
          {showPaymentCols ? '− ซ่อน คอล 27–30' : '+ ส่งงาน · เครดิต · รับเงิน'}
        </Button>
        <Tooltip
          title="คู่มือการใช้งาน (คลิก/ดับเบิลคลิก/คลิกขวา)"
          placement="top"
        >
          <IconButton
            size="small"
            onClick={() => setHelpOpen(true)}
            sx={{ color: 'text.secondary', ml: 0.5 }}
          >
            <HelpOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <TableContainer
        ref={containerRef}
        onMouseDown={onContainerMouseDown}
        sx={{
          maxHeight: '693px',
          overflowX: 'auto',
          overflowY: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: isDragging ? 'none' : 'auto',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)',
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            minWidth: 1500,
            borderCollapse: 'separate',
            borderSpacing: 0,
          }}
        >
          <colgroup>
            <col style={{ width: 38 }} />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <col key={i} style={{ width: getW(i) }} />
            ))}
            {showPoDetail &&
              [7, 8, 9].map((i) => <col key={i} style={{ width: getW(i) }} />)}
            {showPaymentCols &&
              [27, 28, 29, 30].map((i) => (
                <col key={i} style={{ width: getW(i) }} />
              ))}
            {[10, 11, 12, 13, 14, 15, 16, 17, 19, 20].map((i) => (
              <col key={i} style={{ width: getW(i) }} />
            ))}
            <col style={{ width: getW(26) }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...TH_SX,
                  width: 38,
                  textAlign: 'center',
                  fontSize: '0.81rem',
                  lineHeight: 1.2,
                }}
              >
                #<br />
                <span style={{ fontSize: '0.76rem', opacity: 0.7 }}>
                  {displayRows.length}
                </span>
              </TableCell>
              <Tooltip
                title={
                  showTooltip
                    ? sortDir === null
                      ? 'คลิกเรียง A→Z'
                      : sortDir === 'asc'
                      ? 'คลิกเรียง Z→A'
                      : 'คลิกคืนค่าเริ่มต้น'
                    : null
                }
              >
                <TableCell
                  sx={{
                    ...TH_SX,
                    width: getW(1),
                    cursor: 'pointer',
                    userSelect: 'none',
                    ...(sortDir !== null && {
                      bgcolor: 'rgba(192,150,20,0.92)',
                      color: '#fff',
                      borderBottom: '3px solid #d4a017',
                    }),
                    '&:hover': {
                      bgcolor:
                        sortDir !== null ? 'rgba(155,120,15,0.95)' : '#7dd3fc',
                    },
                    ...frzTh(1),
                  }}
                  onClick={cycleSort}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        1{' '}
                      </span>
                      เลขงาน
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortDir === null ? (
                        <SwapVertIcon sx={{ fontSize: 13, opacity: 0.55 }} />
                      ) : sortDir === 'asc' ? (
                        <ArrowDropUpIcon
                          sx={{ fontSize: 40, color: '#ef4444' }}
                        />
                      ) : (
                        <ArrowDropDownIcon
                          sx={{ fontSize: 40, color: '#ef4444' }}
                        />
                      )}
                    </Box>
                  </Box>
                  <ResizeHandle onStart={(e) => startResize(1, e)} />
                </TableCell>
              </Tooltip>
              <Tooltip title="สถานภาพ" placement="top">
                <TableCell sx={{ ...TH_SX, width: getW(2), ...frzTh(2) }}>
                  <Stack
                    direction="row"
                    spacing={0.4}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                      2{' '}
                    </span>
                    <span>สถานะ</span>
                  </Stack>
                  <ResizeHandle onStart={(e) => startResize(2, e)} />
                </TableCell>
              </Tooltip>
              <Tooltip title="ความก้าวหน้า %" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('progress', TH_SX),
                    width: getW(3),
                    ...frzTh(3),
                  }}
                  onClick={() => cycleColSort('progress')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        3{' '}
                      </span>
                      ความคืบหน้า
                    </Box>
                    <Box>%</Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('progress')}</Box>
                  </Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(3, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="ชื่อโครงการ" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('name', TH_SX),
                    width: getW(4),
                    ...frzTh(4),
                  }}
                  onClick={() => cycleColSort('name')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        4{' '}
                      </span>
                      โครงการ
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('name')}</Box>
                  </Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(4, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="ผู้ว่าจ้าง" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('customer', TH_SX),
                    width: getW(5),
                    ...frzTh(5),
                  }}
                  onClick={() => cycleColSort('customer')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        5{' '}
                      </span>
                      ผู้ว่าจ้าง
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('customer')}</Box>
                  </Box>
                  <ResizeHandle onStart={(e) => startResize(5, e)} />
                </TableCell>
              </Tooltip>
              <Tooltip title="มูลค่า PO" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('po', TH_SX),
                    ...TH_HAS_TOT,
                    width: getW(6),
                    ...frzTh(6),
                  }}
                  onClick={() => cycleColSort('po')}
                >
                  <Stack
                    direction="row"
                    spacing={0.4}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                      6{' '}
                    </span>
                    <span>PO</span>
                    {sortIcon('po')}
                  </Stack>
                  <Box sx={TH_TOT}>{fmt(totals.po)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(6, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              {showPoDetail && (
                <>
                  <Tooltip title="วางบิลแล้วรอรับเงิน" placement="top">
                    <TableCell
                      sx={{
                        ...thSortSx('billed_waiting', TH_GROUP_SX, '#6ba8a4'),
                        ...TH_HAS_TOT,
                        width: getW(7),
                      }}
                      onClick={() => cycleColSort('billed_waiting')}
                    >
                      <Stack
                        direction="row"
                        spacing={0.4}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          7{' '}
                        </span>
                        <span>
                          วางบิลแล้วรอรับเงิน
                          <br />
                          <span
                            style={{
                              fontWeight: 500,
                              fontSize: '0.79em',
                              color: '#bd4519',
                            }}
                          >
                            ส่วนที่ส่งงานแล้วจาก PO รอรับเงิน
                          </span>
                        </span>
                        {sortIcon('billed_waiting')}
                      </Stack>
                      <Box sx={TH_TOT}>{fmt(totals.billed)}</Box>
                      <ResizeHandle
                        onStart={(e) => {
                          e.stopPropagation();
                          startResize(7, e);
                        }}
                      />
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="รับเงินมาแล้ว" placement="top">
                    <TableCell
                      sx={{
                        ...thSortSx('received', TH_GROUP_SX, '#6ba8a4'),
                        ...TH_HAS_TOT,
                        width: getW(8),
                      }}
                      onClick={() => cycleColSort('received')}
                    >
                      <Stack
                        direction="row"
                        spacing={0.4}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          8{' '}
                        </span>
                        {/* <span>ยังไม่ได้ส่งงาน</span> */}
                        <span>รับเงินมาแล้ว</span>
                        {sortIcon('received')}
                      </Stack>
                      <Box sx={TH_TOT}>{fmt(totals.received)}</Box>
                      <ResizeHandle
                        onStart={(e) => {
                          e.stopPropagation();
                          startResize(8, e);
                        }}
                      />
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="ยอดค้างส่งงานจาก PO" placement="top">
                    <TableCell
                      sx={{
                        ...thSortSx('po_remaining', TH_GROUP_SX, '#6ba8a4'),
                        ...TH_HAS_TOT,
                        width: getW(9),
                      }}
                      onClick={() => cycleColSort('po_remaining')}
                    >
                      <Stack
                        direction="row"
                        spacing={0.4}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          9{' '}
                        </span>
                        <span>
                          ยอดค้างส่งงานจาก PO
                          <br />
                          <span
                            style={{
                              fontWeight: 500,
                              fontSize: '0.79em',
                              color: '#bd4519',
                            }}
                          >
                            ยังไม่ได้ส่งงาน
                          </span>
                        </span>

                        {sortIcon('po_remaining')}
                      </Stack>
                      <Box
                        sx={{
                          ...TH_TOT,
                          color: totals.poRemain < 0 ? '#b91c1c' : '#082f49',
                        }}
                      >
                        {fmt(totals.poRemain)}
                      </Box>
                      <ResizeHandle
                        onStart={(e) => {
                          e.stopPropagation();
                          startResize(9, e);
                        }}
                      />
                    </TableCell>
                  </Tooltip>
                </>
              )}
              {showPaymentCols && (
                <>
                  <Tooltip title="วันที่ส่งงาน" placement="top">
                    <TableCell sx={{ ...TH_GROUP4_SX, width: getW(27) }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          27{' '}
                        </span>
                        ส่งงาน
                        <br />
                        วันที่
                      </Box>
                      <ResizeHandle onStart={(e) => startResize(27, e)} />
                    </TableCell>
                  </Tooltip>
                  <Tooltip title="เครดิต (วัน) จาก Client" placement="top">
                    <TableCell sx={{ ...TH_GROUP4_SX, width: getW(28) }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          28{' '}
                        </span>
                        เครดิต
                        <br />
                        (วัน)
                      </Box>
                      <ResizeHandle onStart={(e) => startResize(28, e)} />
                    </TableCell>
                  </Tooltip>
                  <Tooltip
                    title="วันในเดือนที่ลูกค้าจ่ายเงิน เช่น 15 = ทุกวันที่ 15"
                    placement="top"
                  >
                    <TableCell sx={{ ...TH_GROUP4_SX, width: getW(31) }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          31{' '}
                        </span>
                        รอบ
                        <br />
                        จ่ายเงิน
                      </Box>
                      <ResizeHandle onStart={(e) => startResize(31, e)} />
                    </TableCell>
                  </Tooltip>
                  <Tooltip
                    title="วันที่คาดรับเงิน = วันส่งงาน + เครดิต (+ รอบจ่าย)"
                    placement="top"
                  >
                    <TableCell sx={{ ...TH_GROUP4_SX, width: getW(29) }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          29{' '}
                        </span>
                        รับเงิน
                        <br />
                        วันที่
                      </Box>
                      <ResizeHandle onStart={(e) => startResize(29, e)} />
                    </TableCell>
                  </Tooltip>
                  <Tooltip
                    title="รับเงินในอีกกี่วัน (ติดลบ = เกินกำหนด)"
                    placement="top"
                  >
                    <TableCell sx={{ ...TH_GROUP4_SX, width: getW(30) }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                          30{' '}
                        </span>
                        รับเงิน
                        <br />
                        อีก(วัน)
                      </Box>
                      <ResizeHandle onStart={(e) => startResize(30, e)} />
                    </TableCell>
                  </Tooltip>
                </>
              )}
              <Tooltip title="ทุนคาดการณ์ = BG (col33) − ใช้ไปแล้ว (col11)" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('projected_fund', TH_GROUP2_SX, '#f9c4af'),
                    ...TH_HAS_TOT,
                    width: getW(10),
                  }}
                  onClick={() => cycleColSort('projected_fund')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        10{' '}
                      </span>
                      ทุน
                      <br />
                      คาดการณ์
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortIcon('projected_fund')}
                    </Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.projectedFund)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(10, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="ใช้ไปแล้ว" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('total_expenses', TH_GROUP2_SX, '#f9c4af'),
                    ...TH_HAS_TOT,
                    width: getW(11),
                  }}
                  onClick={() => cycleColSort('total_expenses')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        11{' '}
                      </span>
                      ใช้ไปแล้ว
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortIcon('total_expenses')}
                    </Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.spent)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(11, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="เจ้าหนี้รอชำระ" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('seed_ap_pending', TH_GROUP2_SX, '#f9c4af'),
                    ...TH_HAS_TOT,
                    width: getW(12),
                  }}
                  onClick={() => cycleColSort('seed_ap_pending')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        12{' '}
                      </span>
                      AP
                      <br />
                      ตั้งต้น
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortIcon('seed_ap_pending')}
                    </Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.apPending)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(12, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip
                title="ยอด expense ที่ยังไม่จ่าย/ไม่ถูกปฏิเสธ (เหมือนคอลัมน์ Expected ใน BudgetTable.js)"
                placement="top"
              >
                <TableCell
                  sx={{
                    ...thSortSx('expected_value', TH_GROUP2_SX, '#f9c4af'),
                    ...TH_HAS_TOT,
                    width: getW(35),
                  }}
                  onClick={() => cycleColSort('expected_value')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        35{' '}
                      </span>
                      รอจ่าย
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortIcon('expected_value')}
                    </Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.expected)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(35, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip
                title="ยอดรวมรายการรอจ่ายที่ยังไม่จ่าย (จาก Budget ทุกรายการของโปรเจกต์นี้)"
                placement="top"
              >
                <TableCell
                  sx={{
                    ...thSortSx('ap_pending_unpaid', TH_GROUP2_SX, '#f9c4af'),
                    ...TH_HAS_TOT,
                    width: getW(32),
                  }}
                  onClick={() => cycleColSort('ap_pending_unpaid')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        32{' '}
                      </span>
                      AP
                      <br />
                      รอจ่าย
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortIcon('ap_pending_unpaid')}
                    </Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.apPendingUnpaid)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(32, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="% Budget ที่ใช้ไปแล้ว" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('bg_used_pct', TH_GROUP2_SX, '#f9c4af'),
                    width: getW(13),
                  }}
                  onClick={() => cycleColSort('bg_used_pct')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        13{' '}
                      </span>
                      %ใช้
                      <br />
                      BG
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('bg_used_pct')}</Box>
                  </Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(13, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="เกิน Budget" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('over_budget', TH_GROUP2_SX, '#f9c4af'),
                    ...TH_HAS_TOT,
                    width: getW(14),
                  }}
                  onClick={() => cycleColSort('over_budget')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        14{' '}
                      </span>
                      เกิน BG
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('over_budget')}</Box>
                  </Box>
                  {totals.col14 < 0 && (
                    <Box sx={{ ...TH_TOT, color: '#b91c1c' }}>
                      {fmt(totals.col14)}
                    </Box>
                  )}
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(14, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="วงเงิน BG (งบประมาณรวมของโปรเจกต์)" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('total_budget', TH_GROUP3_SX, '#fde68a'),
                    ...TH_HAS_TOT,
                    width: getW(33),
                  }}
                  onClick={() => cycleColSort('total_budget')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        33{' '}
                      </span>
                      BG
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('total_budget')}</Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.budget)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(33, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="Budget คงเหลือ" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('net_remaining', TH_GROUP3_SX, '#fde68a'),
                    ...TH_HAS_TOT,
                    width: getW(15),
                  }}
                  onClick={() => cycleColSort('net_remaining')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        15{' '}
                      </span>
                      BG
                      <br />
                      คงเหลือ
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortIcon('net_remaining')}
                    </Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.col15)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(15, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip
                title="เงินมัดจำ/ค่าประกันที่ติดมากับ expense ที่ยังไม่ได้รับคืน (จาก Budget ทุกรายการของโปรเจกต์นี้)"
                placement="top"
              >
                <TableCell
                  sx={{
                    ...thSortSx('refund_pending', TH_GROUP3_SX, '#fde68a'),
                    ...TH_HAS_TOT,
                    width: getW(34),
                  }}
                  onClick={() => cycleColSort('refund_pending')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        34{' '}
                      </span>
                      รอเงินคืน
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      {sortIcon('refund_pending')}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      ...TH_TOT,
                      color: totals.refundPending > 0 ? '#b91c1c' : '#082f49',
                    }}
                  >
                    {totals.refundPending > 0 ? '-' : ''}
                    {fmt(totals.refundPending)}
                  </Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(34, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="ต้องใช้อีก" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('to_spend', TH_GROUP3_SX, '#fde68a'),
                    ...TH_HAS_TOT,
                    width: getW(16),
                  }}
                  onClick={() => cycleColSort('to_spend')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        16{' '}
                      </span>
                      ต้องใช้
                      <br />
                      อีก
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('to_spend')}</Box>
                  </Box>
                  <Box sx={TH_TOT}>{fmt(totals.col16)}</Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(16, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="ยอดเงินรวมที่คาดว่าจะต้องใช้อีก" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('col17', TH_GROUP3_SX, '#fde68a'),
                    ...TH_HAS_TOT,
                    width: getW(17),
                  }}
                  onClick={() => cycleColSort('col17')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        17{' '}
                      </span>
                      ทุนคงเหลือ
                      <br />
                      (คาด)
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('col17')}</Box>
                  </Box>
                  <Box
                    sx={{
                      ...TH_TOT,
                      color: totals.col17 > 0 ? '#b91c1c' : '#082f49',
                    }}
                  >
                    {fmt(totals.col17)}
                  </Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(17, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="กำไรคาดการณ์" placement="top">
                <TableCell
                  sx={{
                    ...TH_GROUP3_SX,
                    ...TH_HAS_TOT,
                    width: getW(19),
                    cursor: 'pointer',
                    userSelect: 'none',
                    ...(profitSort !== null && {
                      bgcolor: 'rgba(192,150,20,0.92)',
                      color: '#fff',
                      borderBottom: '3px solid #d4a017',
                    }),
                    '&:hover': {
                      bgcolor:
                        profitSort !== null
                          ? 'rgba(155,120,15,0.95)'
                          : '#fef08a',
                    },
                  }}
                  onClick={cycleProfitSort}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Stack
                      direction="row"
                      spacing={0.4}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        19{' '}
                      </span>
                      <span>กำไรคาดการณ์</span>
                      {profitSort === null ? (
                        <SwapVertIcon sx={{ fontSize: 13, opacity: 0.55 }} />
                      ) : profitSort === 'asc' ? (
                        <ArrowDropUpIcon
                          sx={{ fontSize: 40, color: '#ef4444' }}
                        />
                      ) : (
                        <ArrowDropDownIcon
                          sx={{ fontSize: 40, color: '#ef4444' }}
                        />
                      )}
                    </Stack>
                    <Box
                      sx={{
                        fontSize: '0.78rem',
                        opacity: 0.65,
                        lineHeight: 1.2,
                        mt: 0.3,
                      }}
                    >
                      6−11−17
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      ...TH_TOT,
                      color: totals.profit < 0 ? '#b91c1c' : '#082f49',
                    }}
                  >
                    {fmt(totals.profit)}
                  </Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(19, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="% กำไร" placement="top">
                <TableCell
                  sx={{
                    ...thSortSx('profit_pct', TH_GROUP3_SX, '#fde68a'),
                    width: getW(20),
                  }}
                  onClick={() => cycleColSort('profit_pct')}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box>
                      <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>
                        20{' '}
                      </span>
                      %<br />
                      กำไร
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>{sortIcon('profit_pct')}</Box>
                  </Box>
                  <ResizeHandle
                    onStart={(e) => {
                      e.stopPropagation();
                      startResize(20, e);
                    }}
                  />
                </TableCell>
              </Tooltip>
              <Tooltip title="หมายเหตุ" placement="top">
                <TableCell sx={{ ...TH_SX, width: getW(26) }}>
                  <span style={{ fontSize: '0.9rem', opacity: 0.55 }}>26 </span>
                  หมายเหตุ
                  <ResizeHandle onStart={(e) => startResize(26, e)} />
                </TableCell>
              </Tooltip>
            </TableRow>
          </TableHead>

          <TableBody
            sx={{ opacity: isStale ? 0.55 : 1, transition: 'opacity 0.18s' }}
          >
            {isLoading &&
              rows.length === 0 &&
              Array.from({ length: 10 }, (_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: showPoDetail ? 20 : 17 }, (_, j) => (
                    <TableCell key={j} sx={{ py: 0.8 }}>
                      <Skeleton
                        animation="wave"
                        height={18}
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {displayRows.map((row, idx) => {
              const po = row.include_vat
                ? (row.cost || 0) - (row.vat || 0)
                : row.cost || 0;
              const received = row.received ?? 0; // col 7
              const billedWaiting = row.billed_waiting ?? 0; // col 8
              const poRemaining = po - received - billedWaiting; // col 9
              const progress = getVal(row, 'progress') ?? 0;
              const note = getVal(row, 'dashboard_note') ?? '';
              const mergedRow = mergedDisplayRows[idx];
              const overBudget = row.over_budget ?? 0;
              const bgUsedPct = row.bg_used_pct ?? 0;
              const isOdd = idx % 2 === 0;
              const rowBg = isOdd ? '#ffffff' : '#f6f8fc';
              const frzTd = (col) =>
                freezeCols
                  ? {
                      position: 'sticky',
                      left: stickyLeftOf[col],
                      zIndex: 1,
                      bgcolor: rowBg,
                    }
                  : {};
              const col14raw =
                (row.total_budget ?? 0) - (row.total_expenses ?? 0);
              const col14 = col14raw < 0 ? col14raw : null; // เกิน BG: แสดงเฉพาะค่าติดลบ
              const { col15, col16, col17 } = getBgColumnValues(
                mergedRow,
                inclBgRemain,
              );
              const realprofit = getEstimatedProfit(mergedRow); // เหมือน estimatedProfitForPercent ใน ProjectPopup.js
              const realPct = po > 0 ? (realprofit / po) * 100 : 0;
              const projectedFund = getProjectedFund(mergedRow); // col10 = col33(BG) - col11(ใช้ไปแล้ว)

              return (
                <Tooltip
                  key={row._id}
                  title={note}
                  followCursor
                  disableHoverListener={!note}
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#d946ef',
                        color: '#fff',
                        fontSize: '0.89rem',
                        maxWidth: 420,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                      },
                    },
                    arrow: { sx: { color: '#d946ef' } },
                  }}
                >
                  <TableRow
                    sx={{
                      bgcolor: isOdd ? '#ffffff' : '#f6f8fc',
                      transition: 'background-color 0.1s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.09),
                      },
                    }}
                  >
                    {/* # */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        textAlign: 'center',
                        color: '#94a3b8',
                        fontSize: '0.81rem',
                        userSelect: 'none',
                      }}
                    >
                      {idx + 1}
                    </TableCell>

                    {/* JOB */}
                    <TableCell sx={{ ...TD_SX, ...frzTd(1) }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color:
                            projectFilter === row.project_number
                              ? '#7c3aed'
                              : 'primary.main',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() =>
                          scheduleClick(() =>
                            setProjectFilter(
                              projectFilter === row.project_number
                                ? ''
                                : row.project_number,
                            ),
                          )
                        }
                        onDoubleClick={() =>
                          cancelAndRun(() => onClickProject?.(row._id))
                        }
                      >
                        {row.project_number || '-'}
                      </Typography>
                    </TableCell>

                    {/* STATUS — 3 dimensions */}
                    {(() => {
                      const ws = getVal(row, 'work_status') || '';
                      const fs = getVal(row, 'finance_status') || '';
                      const lw = getVal(row, 'legal_warranty');
                      const ld = getVal(row, 'legal_debt');
                      const ll = getVal(row, 'legal_lawsuit');
                      const bgUsageCategory =
                        getVal(row, 'bg_usage_category') || '';
                      const workOpt = WORK_OPTIONS.find((o) => o.key === ws);
                      const finOpt = FINANCE_OPTIONS.find((o) => o.key === fs);
                      const bgUsageOpt = BG_USAGE_OPTIONS.find(
                        (o) => o.key === bgUsageCategory,
                      );
                      const hasLegal = lw || ld || ll;
                      return (
                        <TableCell
                          sx={{
                            ...TD_SX,
                            ...frzTd(2),
                            cursor: isAdmin ? 'pointer' : 'default',
                            userSelect: 'none',
                            '&:hover': isAdmin
                              ? { bgcolor: 'rgba(99,102,241,0.07)' }
                              : {},
                            py: 0.5,
                          }}
                          onClick={(e) => {
                            if (!isAdmin) return;
                            setDim3Popover({
                              anchorEl: e.currentTarget,
                              projectId: row._id,
                              work_status: ws,
                              finance_status: fs,
                              bg_usage_category: bgUsageCategory,
                              legal_warranty: lw || false,
                              legal_debt: ld || false,
                              legal_lawsuit: ll || false,
                            });
                          }}
                        >
                          <Stack spacing={0.15} sx={{ pointerEvents: 'none' }}>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                color: workOpt?.color || '#cbd5e1',
                                fontWeight: 400,
                                fontSize: '0.91rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {workOpt?.label || '— งาน —'}
                            </Typography>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                color: finOpt?.color || '#cbd5e1',
                                fontWeight: 400,
                                fontSize: '0.91rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {finOpt?.label || '— การเงิน —'}
                            </Typography>
                            {bgUsageOpt && (
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  color: bgUsageOpt.color,
                                  fontWeight: 400,
                                  fontSize: '0.91rem',
                                  lineHeight: 1.3,
                                }}
                              >
                                {bgUsageOpt.label}
                              </Typography>
                            )}
                            {hasLegal && (
                              <Stack
                                direction="row"
                                spacing={0.4}
                                alignItems="center"
                              >
                                {lw && (
                                  <Tooltip
                                    title="Warrant/Guarantee"
                                    placement="top"
                                    arrow
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: '#7c3aed',
                                          fontSize: '0.9rem',
                                        },
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: '#7c3aed',
                                        pointerEvents: 'auto',
                                        cursor: 'default',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {ld && (
                                  <Tooltip
                                    title="เจ้าหนี้ค้างจ่าย"
                                    placement="top"
                                    arrow
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: '#dc2626',
                                          fontSize: '0.9rem',
                                        },
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: '#dc2626',
                                        pointerEvents: 'auto',
                                        cursor: 'default',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {ll && (
                                  <Tooltip
                                    title="ฟ้องร้อง"
                                    placement="top"
                                    arrow
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: '#991b1b',
                                          fontSize: '0.9rem',
                                        },
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: '#991b1b',
                                        pointerEvents: 'auto',
                                        cursor: 'default',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </TableCell>
                      );
                    })()}

                    {/* % ความก้าวหน้า */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'center', ...frzTd(3) }}
                    >
                      <InlineNumberCell
                        projectId={row._id}
                        field="progress"
                        value={progress}
                        isAdmin={isAdmin}
                        getExtraPatch={(v) => {
                          const ws = getVal(row, 'work_status') || '';
                          if (v === 100 && ws !== 'done')
                            return { work_status: 'done' };
                          if (v < 100 && ws === 'done')
                            return { work_status: 'in_progress' };
                          return {};
                        }}
                        onSaved={(v, extra) => {
                          handleSaveLocal(row._id, 'progress', v);
                          if (extra?.work_status)
                            handleSaveLocal(
                              row._id,
                              'work_status',
                              extra.work_status,
                            );
                        }}
                      />
                    </TableCell>

                    {/* โครงการ */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        width: 165,
                        maxWidth: 165,
                        position: 'relative',
                        ...frzTd(4),
                      }}
                      onMouseLeave={hideNameTooltip}
                    >
                      <Stack
                        direction="row"
                        spacing={0.3}
                        alignItems="flex-start"
                      >
                        <Tooltip
                          title={row.name || ''}
                          placement="top"
                          arrow
                          disableInteractive
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontSize: '0.91rem',
                              cursor: 'pointer',
                              color:
                                projectFilter === row.project_number
                                  ? '#7c3aed'
                                  : !row.total_budget
                                  ? '#1e293b'
                                  : row.color ||
                                    (bgUsedPct > 100
                                      ? '#dc2626'
                                      : bgUsedPct > 50
                                      ? '#d97706'
                                      : 'text.primary'),
                              '&:hover': {
                                color: !row.total_budget
                                  ? '#334155'
                                  : 'primary.main',
                                textDecoration: 'underline',
                              },
                            }}
                            onClick={() =>
                              scheduleClick(() =>
                                setProjectFilter(
                                  projectFilter === row.project_number
                                    ? ''
                                    : row.project_number,
                                ),
                              )
                            }
                            onDoubleClick={() =>
                              cancelAndRun(() => onClickProject?.(row._id))
                            }
                            onContextMenu={(e) =>
                              showNameTooltip(e, row.name || '')
                            }
                          >
                            {row.name || '-'}
                          </Typography>
                        </Tooltip>
                        {col14raw < 0 && (
                          <Tooltip
                            title="งบโครงการเกิน"
                            placement="top"
                            slotProps={{
                              tooltip: {
                                sx: { bgcolor: '#dc2626', fontSize: '0.9rem' },
                              },
                            }}
                          >
                            <FlagIcon
                              sx={{
                                fontSize: 13,
                                color: '#dc2626',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                        {!row.total_budget && (
                          <Tooltip
                            title="no Budget Control"
                            placement="top"
                            slotProps={{
                              tooltip: {
                                sx: { bgcolor: '#dc2626', fontSize: '0.9rem' },
                              },
                            }}
                          >
                            <ErrorOutlineIcon
                              sx={{
                                fontSize: 13,
                                color: '#dc2626',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                      {row.has_over_budget_category && (
                        <Tooltip
                          title={`เกิน BG: ${(
                            row.over_budget_category_names || []
                          ).join(', ')}`}
                          placement="top-start"
                          slotProps={{
                            tooltip: {
                              sx: { bgcolor: '#c2185b', fontSize: '0.9rem' },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              right: 3,
                              bottom: 2,
                              fontSize: '0.9rem',
                              color: '#e91e8c',
                              lineHeight: 1,
                              cursor: 'default',
                            }}
                          >
                            ⚠
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* ผู้ว่าจ้าง */}
                    <TableCell sx={{ ...TD_SX, ...frzTd(5) }}>
                      <Tooltip
                        title={getCustomerName(row) || ''}
                        placement="top"
                        arrow
                        disableInteractive
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontSize: '0.91rem',
                            cursor: 'pointer',
                            color:
                              customerFilter === getCustomerName(row)
                                ? '#1d4ed8'
                                : '#2563eb',
                            '&:hover': {
                              color: '#1d4ed8',
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() =>
                            scheduleClick(() =>
                              setCustomerFilter(
                                customerFilter === getCustomerName(row)
                                  ? ''
                                  : getCustomerName(row),
                              ),
                            )
                          }
                          onDoubleClick={() =>
                            cancelAndRun(() => onClickProject?.(row._id))
                          }
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (row.client_id) openContactModal(row.client_id);
                          }}
                        >
                          {getCustomerName(row) || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* PO */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right', ...frzTd(6) }}
                    >
                      <NumCell value={po} />
                    </TableCell>

                    {showPoDetail &&
                      (() => {
                        const seedReceived = getVal(row, 'seed_received') ?? 0;
                        const seedBilled =
                          getVal(row, 'seed_billed_waiting') ?? 0;
                        const effPoRemaining =
                          po -
                          (received + seedReceived) -
                          (billedWaiting + seedBilled);
                        return (
                          <>
                            {/* col 7: วางบิลแล้วรอรับเงิน + ค่าตั้งต้น */}
                            {renderSeedCell({
                              projectId: row._id,
                              projectName: row.name,
                              field: 'seed_billed_waiting',
                              seedValue: seedBilled,
                              autoValue: billedWaiting,
                            })}
                            {/* col 8: รับเงินมาแล้ว + ค่าตั้งต้น */}
                            {renderSeedCell({
                              projectId: row._id,
                              projectName: row.name,
                              field: 'seed_received',
                              seedValue: seedReceived,
                              autoValue: received,
                            })}
                            {/* col 9: ยอดค้างส่งงานจาก PO */}
                            <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                              <NumCell value={effPoRemaining} redWhenNegative />
                            </TableCell>
                          </>
                        );
                      })()}

                    {/* payment dates group: col 27-31-28-29-30 */}
                    {showPaymentCols &&
                      (() => {
                        const deliveryDate = getVal(row, 'delivery_date');
                        const creditDays = row.client_id?.credit_days;
                        const paymentCycleDay = getVal(
                          row,
                          'payment_cycle_day',
                        );
                        let expectedDate = null;
                        if (
                          deliveryDate &&
                          creditDays != null &&
                          creditDays !== ''
                        ) {
                          const base = new Date(deliveryDate);
                          base.setDate(base.getDate() + Number(creditDays));
                          if (
                            paymentCycleDay != null &&
                            paymentCycleDay !== ''
                          ) {
                            const day = Number(paymentCycleDay);
                            // หาวันรอบจ่ายถัดไปที่ >= base
                            const sameMonth = new Date(
                              base.getFullYear(),
                              base.getMonth(),
                              day,
                            );
                            expectedDate =
                              sameMonth >= base
                                ? sameMonth
                                : new Date(
                                    base.getFullYear(),
                                    base.getMonth() + 1,
                                    day,
                                  );
                          } else {
                            expectedDate = base;
                          }
                        }
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const daysLeft = expectedDate
                          ? Math.round(
                              (expectedDate.getTime() - today.getTime()) /
                                (1000 * 60 * 60 * 24),
                            )
                          : null;
                        const daysColor =
                          daysLeft === null
                            ? 'text.disabled'
                            : daysLeft < 0
                            ? '#dc2626'
                            : daysLeft <= 7
                            ? '#d97706'
                            : '#059669';
                        return (
                          <>
                            {/* col 27 */}
                            <TableCell
                              sx={{
                                ...TD_SX,
                                textAlign: 'center',
                                cursor: isAdmin ? 'pointer' : 'default',
                                userSelect: 'none',
                                '&:hover': isAdmin
                                  ? { bgcolor: 'rgba(5,150,105,0.07)' }
                                  : {},
                              }}
                              onClick={
                                isAdmin
                                  ? (e) => {
                                      e.stopPropagation();
                                      openDeliveryModal(
                                        row._id,
                                        row.name,
                                        deliveryDate,
                                      );
                                    }
                                  : undefined
                              }
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.91rem',
                                  color: deliveryDate
                                    ? 'text.primary'
                                    : 'text.disabled',
                                }}
                              >
                                {fmtDate(deliveryDate) ||
                                  (isAdmin ? 'คลิกกำหนด' : '-')}
                              </Typography>
                            </TableCell>
                            {/* col 28 */}
                            <TableCell sx={{ ...TD_SX, textAlign: 'center' }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.91rem',
                                  color:
                                    creditDays != null && creditDays !== ''
                                      ? 'text.primary'
                                      : 'text.disabled',
                                }}
                              >
                                {creditDays != null && creditDays !== ''
                                  ? `${creditDays}`
                                  : '-'}
                              </Typography>
                            </TableCell>
                            {/* col 31 — รอบการจ่ายเงิน (วันที่ในเดือน) */}
                            <TableCell
                              sx={{
                                ...TD_SX,
                                textAlign: 'center',
                                cursor: isAdmin ? 'pointer' : 'default',
                                userSelect: 'none',
                                '&:hover': isAdmin
                                  ? { bgcolor: 'rgba(5,150,105,0.07)' }
                                  : {},
                              }}
                              onClick={
                                isAdmin
                                  ? (e) => {
                                      e.stopPropagation();
                                      const val = window.prompt(
                                        'รอบการจ่ายเงิน — ใส่วันที่ในเดือน (1-31) หรือว่างเพื่อล้างค่า',
                                        paymentCycleDay != null
                                          ? String(paymentCycleDay)
                                          : '',
                                      );
                                      if (val === null) return; // cancel
                                      const num =
                                        val.trim() === ''
                                          ? null
                                          : Number(val.trim());
                                      if (
                                        num !== null &&
                                        (isNaN(num) || num < 1 || num > 31)
                                      ) {
                                        alert('กรุณาใส่ตัวเลข 1-31 เท่านั้น');
                                        return;
                                      }
                                      dispatch(
                                        actions.projectPatchDashboard(row._id, {
                                          payment_cycle_day: num,
                                        }),
                                      );
                                      handleSaveLocal(
                                        row._id,
                                        'payment_cycle_day',
                                        num,
                                      );
                                      onRowUpdated?.(row._id, {
                                        payment_cycle_day: num,
                                      });
                                    }
                                  : undefined
                              }
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.91rem',
                                  color:
                                    paymentCycleDay != null &&
                                    paymentCycleDay !== ''
                                      ? 'text.primary'
                                      : 'text.disabled',
                                  fontWeight:
                                    paymentCycleDay != null &&
                                    paymentCycleDay !== ''
                                      ? 600
                                      : 400,
                                }}
                              >
                                {paymentCycleDay != null &&
                                paymentCycleDay !== ''
                                  ? `ทุกที่ ${paymentCycleDay}`
                                  : isAdmin
                                  ? 'คลิกกำหนด'
                                  : '-'}
                              </Typography>
                            </TableCell>
                            {/* col 29 */}
                            <TableCell sx={{ ...TD_SX, textAlign: 'center' }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.91rem',
                                  color: expectedDate
                                    ? 'text.primary'
                                    : 'text.disabled',
                                }}
                              >
                                {expectedDate ? fmtDate(expectedDate) : '-'}
                              </Typography>
                            </TableCell>
                            {/* col 30 */}
                            <TableCell sx={{ ...TD_SX, textAlign: 'center' }}>
                              {daysLeft !== null ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: daysLeft < 0 ? 700 : 600,
                                    fontSize: '0.91rem',
                                    color: daysColor,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {daysLeft < 0
                                    ? `เกิน ${-daysLeft}`
                                    : daysLeft === 0
                                    ? 'วันนี้'
                                    : `+${daysLeft}`}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.91rem',
                                    color: 'text.disabled',
                                  }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        );
                      })()}

                    {/* ทุนคาดการณ์ = BG(col33) - ใช้ไปแล้ว(col11) */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={projectedFund} />
                      {po > 0 &&
                        projectedFund > 0 &&
                        (() => {
                          const pct = (projectedFund / po) * 100;
                          return (
                            <Typography
                              variant="caption"
                              sx={{
                                color: pct > 70 ? 'error.main' : 'primary.main',
                                display: 'block',
                                textAlign: 'right',
                                fontSize: '0.84rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {pct.toFixed(1)}%
                            </Typography>
                          );
                        })()}
                    </TableCell>

                    {/* ใช้ไปแล้ว (Paid) */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={getPaidValue(row)} />
                    </TableCell>

                    {/* เจ้าหนี้รอชำระ — ค่าตั้งต้น (auto ในอนาคต) */}
                    {renderSeedCell({
                      projectId: row._id,
                      projectName: row.name,
                      field: 'seed_ap_pending',
                      seedValue: getVal(row, 'seed_ap_pending') ?? 0,
                      autoValue: 0,
                      alwaysRed: true,
                    })}

                    {/* รอจ่าย (col35) — เหมือนคอลัมน์ Expected ใน BudgetTable.js */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={getExpectedValue(row)} />
                    </TableCell>

                    {/* AP รอจ่าย — รวม pending_items ที่ยังไม่จ่ายจากทุก budget ของโปรเจกต์นี้ — ดับเบิ้ลคลิกดู/เพิ่ม/แก้ไขรายการย่อย */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right', cursor: 'pointer' }}
                      onDoubleClick={() => openBudgetListModal(row)}
                    >
                      <NumCell value={row.ap_pending_unpaid} redWhenPositive />
                    </TableCell>

                    {/* % BG ใช้ */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <PctCell
                        value={bgUsedPct}
                        redWhenOver={100}
                        orangeWhenOver={50}
                      />
                    </TableCell>

                    {/* col14: เกิน BG = col10-col11 เฉพาะค่า < 0 */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      {col14 !== null ? (
                        <NumCell value={col14} redWhenNegative />
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.91rem',
                            color: '#cbd5e1',
                            display: 'block',
                            textAlign: 'right',
                          }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* col33: BG (วงเงิน = total_budget) */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={row.total_budget} />
                    </TableCell>

                    {/* col15: BG คงเหลือ = max(col10 - col35 - col32 + col34, 0) */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={col15} />
                    </TableCell>

                    {/* col34: รอเงินคืน */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={getRefundPendingValue(row)} redWhenPositive />
                    </TableCell>

                    {/* col16: ต้องใช้อีก — user editable */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <InlineAmountCell
                        projectId={row._id}
                        field="to_spend"
                        value={col16}
                        isAdmin={isAdmin}
                        onSaved={(v) => {
                          handleSaveLocal(row._id, 'to_spend', v);
                          onRowUpdated?.(row._id, { to_spend: v });
                        }}
                        alwaysRed
                      />
                    </TableCell>

                    {/* col17: ยอดเงินรวมที่คาดว่าจะต้องใช้อีก */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={col17} redWhenPositive />
                    </TableCell>

                    {/* กำไร */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <NumCell value={realprofit} redWhenNegative bold />
                    </TableCell>

                    {/* % กำไร */}
                    <TableCell sx={{ ...TD_SX, textAlign: 'right' }}>
                      <PctCell value={realPct} redWhenNegative />
                    </TableCell>

                    {/* หมายเหตุ */}
                    <TableCell sx={TD_SX}>
                      <InlineNoteCell
                        projectId={row._id}
                        value={note}
                        isAdmin={isAdmin}
                        onSaved={(v) =>
                          handleSaveLocal(row._id, 'dashboard_note', v)
                        }
                      />
                    </TableCell>
                  </TableRow>
                </Tooltip>
              );
            })}

            {displayRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={(showPoDetail ? 24 : 21) - (showPaymentCols ? 0 : 5)}
                  sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}
                >
                  ไม่พบข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Seed value modal — กำหนดค่าตั้งต้น col 7, 8, 12 */}
      {seedModal && (
        <Dialog
          open={!!seedModal}
          onClose={() => {
            if (!seedSaving) setSeedModal(null);
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)',
              px: 3,
              py: 2.5,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ color: '#fff', fontWeight: 700 }}
            >
              กำหนดค่าตั้งต้น
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}
            >
              {seedModal.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.55)',
                display: 'block',
                mt: 0.3,
                fontSize: '0.86rem',
              }}
              noWrap
            >
              {seedModal.projectName}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 2.5, pb: 1 }}>
            {Number(seedModal.autoValue) > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: '#f0f9ff',
                  borderRadius: 2,
                  border: '1px solid #bae6fd',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: '#0369a1', display: 'block', mb: 0.5 }}
                >
                  ค่าจากเอกสารในระบบ
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: '#0369a1' }}
                >
                  {fmt(seedModal.autoValue)}
                </Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.8 }}
            >
              ค่าตั้งต้น (สำหรับข้อมูลเก่าก่อนระบบ)
            </Typography>
            <TextField
              inputRef={seedInputRef}
              fullWidth
              size="small"
              type="number"
              value={seedDraft}
              onChange={(e) => setSeedDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveSeedModal();
                if (e.key === 'Escape') setSeedModal(null);
              }}
              inputProps={{ min: 0, step: 'any' }}
              disabled={seedSaving}
              sx={{
                '& input': {
                  textAlign: 'right',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                },
              }}
              autoFocus
            />
            {(() => {
              const combined =
                (Number(seedModal.autoValue) || 0) + (Number(seedDraft) || 0);
              return (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    รวมทั้งหมด
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: combined < 0 ? 'error.main' : 'primary.main',
                    }}
                  >
                    {fmt(combined)}
                  </Typography>
                </Box>
              );
            })()}
          </DialogContent>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSeedModal(null)}
              disabled={seedSaving}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={saveSeedModal}
              disabled={seedSaving}
              startIcon={
                seedSaving ? (
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                ) : (
                  <SaveIcon />
                )
              }
            >
              บันทึก
            </Button>
          </Box>
        </Dialog>
      )}

      {/* Delivery date modal — col 27 */}
      {deliveryModal && (
        <Dialog
          open={!!deliveryModal}
          onClose={() => {
            if (!deliverySaving) setDeliveryModal(null);
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #065f46 0%, #059669 100%)',
              px: 3,
              py: 2.5,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ color: '#fff', fontWeight: 700 }}
            >
              วันที่ส่งงาน
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                display: 'block',
                mt: 0.3,
                fontSize: '0.86rem',
              }}
              noWrap
            >
              {deliveryModal.projectName}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 2.5, pb: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
            >
              เลือกวันที่ส่งมอบงาน
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={deliveryDraft}
              onChange={(e) => setDeliveryDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveDeliveryModal();
                if (e.key === 'Escape') setDeliveryModal(null);
              }}
              disabled={deliverySaving}
              autoFocus
              InputLabelProps={{ shrink: true }}
              sx={{ '& input': { fontSize: '1rem', fontWeight: 600 } }}
            />
            {deliveryDraft && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  bgcolor: '#ecfdf5',
                  borderRadius: 2,
                  border: '1px solid #a7f3d0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: '#065f46', display: 'block' }}
                >
                  วันที่เลือก: {fmtDate(deliveryDraft)}
                </Typography>
              </Box>
            )}
            {deliveryDraft && (
              <Box sx={{ mt: 1, textAlign: 'right' }}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setDeliveryDraft('')}
                  sx={{ fontSize: '0.86rem' }}
                >
                  ล้างวันที่
                </Button>
              </Box>
            )}
          </DialogContent>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDeliveryModal(null)}
              disabled={deliverySaving}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={saveDeliveryModal}
              disabled={deliverySaving}
              startIcon={
                deliverySaving ? (
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                ) : (
                  <SaveIcon />
                )
              }
              sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
            >
              บันทึก
            </Button>
          </Box>
        </Dialog>
      )}

      {/* Budget list modal — ดับเบิ้ลคลิก col 32 AP รอจ่าย */}
      {budgetListModal && (
        <Dialog
          open={!!budgetListModal}
          onClose={closeBudgetListModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #92400e 0%, #ed6c02 100%)',
              px: 3,
              py: 2.5,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ color: '#fff', fontWeight: 700 }}
            >
              AP รอจ่าย — รายการ Budget
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}
              noWrap
            >
              {budgetListModal.projectNumber} {budgetListModal.projectName}
            </Typography>
          </Box>
          <DialogContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fef3e2' }}>
                    <TableCell sx={{ fontWeight: 700 }} width={48}>
                      No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      AP Pending
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      AP Success
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700 }}
                      align="center"
                      width={110}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {_.isEmpty(budgetListModal.budgets) ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ color: 'text.secondary', py: 3 }}
                      >
                        โปรเจกต์นี้ยังไม่มี budget
                      </TableCell>
                    </TableRow>
                  ) : (
                    budgetListModal.budgets.map((b, idx) => {
                      const unpaidItems = (b.pending_items || []).filter(
                        (i) => !i.paid,
                      );
                      const paidItems = (b.pending_items || []).filter(
                        (i) => i.paid,
                      );
                      const unpaidTotal = unpaidItems.reduce(
                        (s, i) => s + (Number(i.amount) || 0),
                        0,
                      );
                      const paidTotal = paidItems.reduce(
                        (s, i) => s + (Number(i.amount) || 0),
                        0,
                      );
                      return (
                        <TableRow key={b.id || b._id} hover>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell>
                            {b.prefix}
                            {b.budget_number} — {b.name}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color:
                                unpaidTotal > 0 ? '#ed6c02' : 'text.disabled',
                              fontWeight: 600,
                            }}
                          >
                            {fmt(unpaidTotal)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color:
                                paidTotal > 0 ? PAID_COLOR : 'text.disabled',
                              fontWeight: 600,
                            }}
                          >
                            {fmt(paidTotal)}
                          </TableCell>
                          <TableCell align="center">
                            <Badge
                              badgeContent={unpaidItems.length}
                              color="warning"
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={
                                  <HourglassEmptyIcon fontSize="small" />
                                }
                                onClick={() => openPendingBudgetModal(b)}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                รายการ
                              </Button>
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <Box
            sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={closeBudgetListModal}
            >
              ปิด
            </Button>
          </Box>
        </Dialog>
      )}

      <PendingItemsModal
        open={!!pendingBudget}
        budget={pendingBudget}
        onClose={closePendingBudgetModal}
        onUpdated={(updated) => {
          setPendingBudget(updated);
          handleBudgetPendingUpdated(updated);
        }}
      />

      {/* Contact detail modal — right-click on ผู้ว่าจ้าง */}
      <Dialog
        open={!!contactModal}
        onClose={() => {
          if (!saving) {
            setContactModal(null);
            setEditMode(false);
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* ── Gradient header ── */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #0f766e 100%)',
            px: 4,
            py: 3,
            position: 'relative',
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            {!editMode && (
              <Tooltip title="แก้ไขข้อมูล">
                <IconButton
                  onClick={handleEditStart}
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    '&:hover': {
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.12)',
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              onClick={() => {
                setContactModal(null);
                setEditMode(false);
              }}
              disabled={saving}
              sx={{
                color: 'rgba(255,255,255,0.6)',
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar
              sx={{
                width: 68,
                height: 68,
                bgcolor: 'rgba(255,255,255,0.18)',
                border: '2px solid rgba(255,255,255,0.35)',
                fontSize: '1.8rem',
                fontWeight: 800,
                color: '#fff',
              }}
            >
              {((editMode ? editForm.name : contactModal?.name) ||
                '?')[0].toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              {!editMode && contactModal?.contact_id && (
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color="#fff"
                  sx={{ letterSpacing: 1, lineHeight: 1.1 }}
                >
                  {contactModal.contact_id}
                </Typography>
              )}
              <Typography
                variant={contactModal?.contact_id ? 'body1' : 'h5'}
                fontWeight={contactModal?.contact_id ? 400 : 700}
                color={
                  contactModal?.contact_id ? 'rgba(255,255,255,0.75)' : '#fff'
                }
                noWrap
                sx={{ mt: contactModal?.contact_id ? 0.25 : 0 }}
              >
                {editMode ? editForm.name || '-' : contactModal?.name || '-'}
              </Typography>
              {!editMode && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  sx={{ mt: 0.75 }}
                >
                  {contactModal?.contact_type && (
                    <Chip
                      label={contactModal.contact_type}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.96rem',
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: '#fff',
                      }}
                    />
                  )}
                  {contactModal?.role && (
                    <Chip
                      label={contactModal.role}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.96rem',
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: '#fff',
                      }}
                    />
                  )}
                </Stack>
              )}
              {editMode && (
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}
                >
                  โหมดแก้ไข
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
          {editMode ? (
            /* ── Edit form ── */
            <Stack spacing={0} divider={<Divider />}>
              <Box sx={{ px: 4, py: 3, bgcolor: '#fff' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  ข้อมูลบริษัท
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="ชื่อบริษัท / ผู้ว่าจ้าง"
                    value={editForm.name || ''}
                    onChange={handleFormChange('name')}
                    fullWidth
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="รหัส"
                      value={editForm.contact_id || ''}
                      onChange={handleFormChange('contact_id')}
                    />
                    <TextField
                      label="ประเภท"
                      value={editForm.contact_type || ''}
                      onChange={handleFormChange('contact_type')}
                    />
                    <TextField
                      label="บทบาท"
                      value={editForm.role || ''}
                      onChange={handleFormChange('role')}
                    />
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ px: 4, py: 3, bgcolor: '#fff' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  ที่อยู่
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="ที่อยู่ 1"
                    value={editForm.address1 || ''}
                    onChange={handleFormChange('address1')}
                    fullWidth
                  />
                  <TextField
                    label="ที่อยู่ 2"
                    value={editForm.address2 || ''}
                    onChange={handleFormChange('address2')}
                    fullWidth
                  />
                  <TextField
                    label="ที่อยู่ 3"
                    value={editForm.address3 || ''}
                    onChange={handleFormChange('address3')}
                    fullWidth
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="รหัสไปรษณีย์"
                      value={editForm.zipcode || ''}
                      onChange={handleFormChange('zipcode')}
                    />
                    <TextField
                      label="สถานที่ประกอบการ"
                      value={editForm.business_location || ''}
                      onChange={handleFormChange('business_location')}
                    />
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ px: 4, py: 3, bgcolor: '#fff' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  ข้อมูลนิติบุคคล
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="เลขผู้เสียภาษี"
                    value={editForm.taxId || ''}
                    onChange={handleFormChange('taxId')}
                    fullWidth
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="ชื่อสาขา"
                      value={editForm.office_branch || ''}
                      onChange={handleFormChange('office_branch')}
                    />
                    <TextField
                      label="รหัสสาขา"
                      value={editForm.branch_code || ''}
                      onChange={handleFormChange('branch_code')}
                    />
                    <TextField
                      label="เครดิต (วัน)"
                      value={editForm.credit_days || ''}
                      onChange={handleFormChange('credit_days')}
                    />
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ px: 4, py: 3, bgcolor: '#fff' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    mb: 2,
                  }}
                >
                  ข้อมูลติดต่อ
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="ชื่อผู้ติดต่อ"
                    value={editForm.contact_name || ''}
                    onChange={handleFormChange('contact_name')}
                    fullWidth
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="มือถือ"
                      value={editForm.mobile || ''}
                      onChange={handleFormChange('mobile')}
                    />
                    <TextField
                      label="โทรศัพท์สำนักงาน"
                      value={editForm.office_phone || ''}
                      onChange={handleFormChange('office_phone')}
                    />
                    <TextField
                      label="แฟกซ์"
                      value={editForm.fax || ''}
                      onChange={handleFormChange('fax')}
                    />
                    <TextField
                      label="อีเมล"
                      value={editForm.email || ''}
                      onChange={handleFormChange('email')}
                    />
                  </Box>
                </Stack>
              </Box>

              {saveError && (
                <Box sx={{ px: 4, py: 2, bgcolor: '#fef2f2' }}>
                  <Typography variant="body2" color="error">
                    {saveError}
                  </Typography>
                </Box>
              )}

              <Box sx={{ px: 4, py: 2.5, bgcolor: '#f8fafc' }}>
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleEditCancel}
                    disabled={saving}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      saving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          ) : (
            /* ── View mode ── */
            <Stack divider={<Divider />}>
              {(contactModal?.address1 ||
                contactModal?.address2 ||
                contactModal?.address3 ||
                contactModal?.business_location) && (
                <Box sx={{ px: 4, py: 2.5, bgcolor: '#fff' }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <LocationOnIcon
                      sx={{
                        fontSize: 22,
                        color: 'primary.main',
                        mt: 0.2,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
                      >
                        ที่อยู่
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {[
                          contactModal?.address1,
                          contactModal?.address2,
                          contactModal?.address3,
                          contactModal?.zipcode,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </Typography>
                      {contactModal?.business_location && (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ mt: 0.75 }}
                        >
                          <BusinessIcon
                            sx={{ fontSize: 16, color: 'text.disabled' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {contactModal.business_location}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}
              {(contactModal?.taxId ||
                contactModal?.office_branch ||
                contactModal?.branch_code ||
                (contactModal?.credit_days != null &&
                  contactModal?.credit_days !== '')) && (
                <Box sx={{ px: 4, py: 2.5, bgcolor: '#fff' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      display: 'block',
                      mb: 1.5,
                    }}
                  >
                    ข้อมูลนิติบุคคล
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0}
                    sx={{ flexWrap: 'wrap', gap: 2 }}
                  >
                    {contactModal?.taxId && (
                      <Paper
                        variant="outlined"
                        sx={{ px: 2, py: 1.25, borderRadius: 2, minWidth: 170 }}
                      >
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          display="block"
                        >
                          เลขผู้เสียภาษี
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ letterSpacing: 0.5 }}
                        >
                          {contactModal.taxId}
                        </Typography>
                      </Paper>
                    )}
                    {(contactModal?.office_branch ||
                      contactModal?.branch_code) && (
                      <Paper
                        variant="outlined"
                        sx={{ px: 2, py: 1.25, borderRadius: 2, minWidth: 140 }}
                      >
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          display="block"
                        >
                          สาขา
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {[
                            contactModal.office_branch,
                            contactModal.branch_code
                              ? `(${contactModal.branch_code})`
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        </Typography>
                      </Paper>
                    )}
                    {contactModal?.credit_days != null &&
                      contactModal?.credit_days !== '' && (
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            py: 1.25,
                            borderRadius: 2,
                            minWidth: 100,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <CreditScoreIcon
                              sx={{ fontSize: 16, color: 'text.disabled' }}
                            />
                            <Typography variant="body2" color="text.disabled">
                              เครดิต
                            </Typography>
                          </Stack>
                          <Typography variant="body1" fontWeight={600}>
                            {contactModal.credit_days} วัน
                          </Typography>
                        </Paper>
                      )}
                  </Stack>
                </Box>
              )}
              {(contactModal?.contact_name ||
                contactModal?.mobile ||
                contactModal?.office_phone ||
                contactModal?.fax ||
                contactModal?.email) && (
                <Box sx={{ px: 4, py: 2.5, bgcolor: '#fff' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      display: 'block',
                      mb: 1.5,
                    }}
                  >
                    ข้อมูลติดต่อ
                  </Typography>
                  <Stack spacing={1.5}>
                    {contactModal?.contact_name && (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <PersonIcon
                          sx={{
                            fontSize: 20,
                            color: 'text.disabled',
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography variant="body2" color="text.disabled">
                            ผู้ติดต่อ
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {contactModal.contact_name}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                    {(contactModal?.mobile || contactModal?.office_phone) && (
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <PhoneIcon
                          sx={{
                            fontSize: 20,
                            color: 'text.disabled',
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        />
                        <Stack spacing={0.5}>
                          {contactModal?.mobile && (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                component="span"
                              >
                                มือถือ{' '}
                              </Typography>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                component="span"
                              >
                                {contactModal.mobile}
                              </Typography>
                            </Box>
                          )}
                          {contactModal?.office_phone && (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                component="span"
                              >
                                สำนักงาน{' '}
                              </Typography>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                component="span"
                              >
                                {contactModal.office_phone}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    )}
                    {contactModal?.fax && (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <PrintIcon
                          sx={{
                            fontSize: 20,
                            color: 'text.disabled',
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.disabled"
                            component="span"
                          >
                            แฟกซ์{' '}
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            component="span"
                          >
                            {contactModal.fax}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                    {contactModal?.email && (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <AlternateEmailIcon
                          sx={{
                            fontSize: 20,
                            color: 'text.disabled',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" fontWeight={600}>
                          {contactModal.email}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              )}
              {(contactModal?.createdAt || contactModal?.updatedAt) && (
                <Box sx={{ px: 4, py: 2, bgcolor: '#f1f5f9' }}>
                  <Stack direction="row" spacing={4}>
                    {contactModal?.createdAt && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          display="block"
                        >
                          สร้างเมื่อ
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(contactModal.createdAt).toLocaleDateString(
                            'th-TH',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            },
                          )}
                        </Typography>
                      </Box>
                    )}
                    {contactModal?.updatedAt && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          display="block"
                        >
                          แก้ไขล่าสุด
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(contactModal.updatedAt).toLocaleDateString(
                            'th-TH',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            },
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Status context menu */}
      <Menu
        open={Boolean(statusMenu)}
        onClose={() => setStatusMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          statusMenu
            ? { top: statusMenu.mouseY, left: statusMenu.mouseX }
            : undefined
        }
      >
        {Object.entries(STATUS_MAP).map(([key, s]) => (
          <MenuItem
            key={key}
            selected={key === statusMenu?.current}
            onClick={() => handleStatusChange(key)}
            sx={{ fontSize: '0.96rem', gap: 1 }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: s.color,
                flexShrink: 0,
                display: 'inline-block',
              }}
            />
            {s.label}
          </MenuItem>
        ))}
      </Menu>

      {/* ── 3-Dimension Status Popover ── */}
      <Popover
        open={Boolean(dim3Popover)}
        anchorEl={dim3Popover?.anchorEl}
        onClose={() => setDim3Popover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1.5,
            minWidth: 220,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
          },
        }}
      >
        {dim3Popover && (
          <Stack spacing={1.5}>
            {/* ด้านงาน */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#1e3a8a',
                  display: 'block',
                  mb: 0.5,
                  borderBottom: '1px solid #e2e8f0',
                  pb: 0.3,
                }}
              >
                ด้านงาน
              </Typography>
              <Stack spacing={0.3}>
                {WORK_OPTIONS.map((opt) => {
                  const selected = dim3Popover.work_status === opt.key;
                  const isAuto =
                    selected &&
                    dim3Popover._autofilled?.includes('work_status');
                  return (
                    <Box
                      key={opt.key}
                      onClick={() =>
                        handleDim3Change('work_status', selected ? '' : opt.key)
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        py: 0.3,
                        px: 0.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        bgcolor: selected ? `${opt.color}15` : 'transparent',
                        '&:hover': { bgcolor: `${opt.color}10` },
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          border: `2px solid ${
                            selected ? opt.color : '#cbd5e1'
                          }`,
                          bgcolor: selected ? opt.color : 'transparent',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {selected && (
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              bgcolor: '#fff',
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: selected ? opt.color : 'text.primary',
                          fontWeight: selected ? 700 : 400,
                          fontSize: '0.91rem',
                        }}
                      >
                        {opt.label}
                      </Typography>
                      {isAuto && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.76rem',
                            color: '#6366f1',
                            bgcolor: '#eef2ff',
                            px: 0.5,
                            borderRadius: 0.5,
                            lineHeight: 1.6,
                            ml: 0.3,
                          }}
                        >
                          auto
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {/* ด้านการเงิน */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#92400e',
                  display: 'block',
                  mb: 0.5,
                  borderBottom: '1px solid #e2e8f0',
                  pb: 0.3,
                }}
              >
                ด้านการเงิน
              </Typography>
              <Stack spacing={0.3}>
                {FINANCE_OPTIONS.map((opt) => {
                  const selected = dim3Popover.finance_status === opt.key;
                  return (
                    <Box
                      key={opt.key}
                      onClick={() =>
                        handleDim3Change(
                          'finance_status',
                          selected ? '' : opt.key,
                        )
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        py: 0.3,
                        px: 0.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        bgcolor: selected ? `${opt.color}15` : 'transparent',
                        '&:hover': { bgcolor: `${opt.color}10` },
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          border: `2px solid ${
                            selected ? opt.color : '#cbd5e1'
                          }`,
                          bgcolor: selected ? opt.color : 'transparent',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {selected && (
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              bgcolor: '#fff',
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: selected ? opt.color : 'text.primary',
                          fontWeight: selected ? 700 : 400,
                          fontSize: '0.91rem',
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {/* หมวดการใช้ BUDGET */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#374151',
                  display: 'block',
                  mb: 0.5,
                  borderBottom: '1px solid #d1d5db',
                  pb: 0.3,
                }}
              >
                หมวดการใช้ BUDGET
              </Typography>
              <Stack spacing={0.3}>
                {BG_USAGE_OPTIONS.map((opt) => {
                  const selected = dim3Popover.bg_usage_category === opt.key;
                  const bgUsageDisabled = dim3Popover.work_status !== 'done';
                  return (
                    <Tooltip
                      key={opt.key}
                      title={
                        bgUsageDisabled ? 'งานยังไม่เสร็จ 100% เลือกไม่ได้' : ''
                      }
                      placement="right"
                    >
                      <Box
                        onClick={() =>
                          !bgUsageDisabled &&
                          handleDim3Change(
                            'bg_usage_category',
                            selected ? '' : opt.key,
                          )
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          py: 0.3,
                          px: 0.5,
                          cursor: bgUsageDisabled ? 'not-allowed' : 'pointer',
                          borderRadius: 1,
                          bgcolor: selected ? `${opt.color}15` : 'transparent',
                          opacity: bgUsageDisabled ? 0.5 : 1,
                          '&:hover': bgUsageDisabled
                            ? {}
                            : { bgcolor: `${opt.color}10` },
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            border: `2px solid ${
                              selected ? opt.color : '#cbd5e1'
                            }`,
                            bgcolor: selected ? opt.color : 'transparent',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {selected && (
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                bgcolor: '#fff',
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: selected ? opt.color : 'text.primary',
                            fontWeight: selected ? 700 : 400,
                            fontSize: '0.91rem',
                          }}
                        >
                          {opt.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>

            {/* ด้านนิติกรรม */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#6b21a8',
                  display: 'block',
                  mb: 0.5,
                  borderBottom: '1px solid #e2e8f0',
                  pb: 0.3,
                }}
              >
                ด้านนิติกรรม
              </Typography>
              <Stack spacing={0.3}>
                {LEGAL_OPTIONS.map((opt) => {
                  const selected = Boolean(dim3Popover[opt.key]);
                  // Warranty ติ๊กได้เฉพาะเมื่องานเสร็จ 100% แล้ว
                  const warrantyDisabled =
                    opt.key === 'legal_warranty' &&
                    dim3Popover.work_status !== 'done';
                  return (
                    <Tooltip
                      key={opt.key}
                      title={
                        warrantyDisabled
                          ? 'Warranty ได้เฉพาะเมื่องานเสร็จ 100%'
                          : ''
                      }
                      placement="right"
                    >
                      <Box
                        onClick={() =>
                          !warrantyDisabled &&
                          handleDim3Change(opt.key, !selected)
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          py: 0.3,
                          px: 0.5,
                          cursor: warrantyDisabled ? 'not-allowed' : 'pointer',
                          borderRadius: 1,
                          opacity: warrantyDisabled ? 0.35 : 1,
                          bgcolor: selected ? `${opt.color}15` : 'transparent',
                          '&:hover': !warrantyDisabled
                            ? { bgcolor: `${opt.color}10` }
                            : {},
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 0.5,
                            border: `2px solid ${
                              selected ? opt.color : '#cbd5e1'
                            }`,
                            bgcolor: selected ? opt.color : 'transparent',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {selected && (
                            <Box
                              sx={{
                                width: 6,
                                height: 4,
                                borderLeft: '2px solid #fff',
                                borderBottom: '2px solid #fff',
                                transform:
                                  'rotate(-45deg) translate(1px, -1px)',
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: selected ? opt.color : 'text.primary',
                            fontWeight: selected ? 700 : 400,
                            fontSize: '0.91rem',
                          }}
                        >
                          {opt.label}
                        </Typography>
                        {!selected &&
                          opt.key === 'legal_warranty' &&
                          dim3Popover._autofilled?.includes(
                            'legal_warranty',
                          ) && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.76rem',
                                color: '#6366f1',
                                bgcolor: '#eef2ff',
                                px: 0.5,
                                borderRadius: 0.5,
                                lineHeight: 1.6,
                                ml: 0.3,
                              }}
                            >
                              auto
                            </Typography>
                          )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>

            {/* ภาระผูกพัน auto */}
            {(() => {
              const hc = computeHasCommitment({
                work_status: dim3Popover.work_status,
                finance_status: dim3Popover.finance_status,
                legal_warranty: dim3Popover.legal_warranty,
                legal_debt: dim3Popover.legal_debt,
                legal_lawsuit: dim3Popover.legal_lawsuit,
              });
              return (
                <Box
                  sx={{
                    pt: 0.5,
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    ภาระผูกพัน:
                  </Typography>
                  <Chip
                    size="small"
                    label={hc ? 'มีภาระผูกพัน' : 'ไม่มีภาระผูกพัน'}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.86rem',
                      bgcolor: hc ? '#fef3c7' : '#dcfce7',
                      color: hc ? '#b45309' : '#059669',
                    }}
                  />
                </Box>
              );
            })()}
          </Stack>
        )}
      </Popover>

      {/* ── Filter Popover — status chips ── */}
      <Popover
        open={filterOpen}
        anchorEl={containerRef.current}
        onClose={() => setFilterOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1.5,
            maxWidth: 860,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            borderRadius: 2,
          },
        }}
      >
        {statusSummaryChips}
      </Popover>

      {/* ── Help Dialog ── */}
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ color: '#fff', fontWeight: 700 }}
            >
              คู่มือการใช้งาน
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.65)' }}
            >
              คลิกซ้าย / ดับเบิลคลิก / คลิกขวา ในแต่ละ Column
            </Typography>
          </Box>
          <IconButton
            onClick={() => setHelpOpen(false)}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: '#fff' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {(() => {
            const rows = [
              {
                col: 'Header ทุกคอลัมน์',
                left: 'เรียงลำดับ ↑↓ (กดวนซ้ำ)',
                dbl: '—',
                right: '—',
                admin: false,
              },
              {
                col: '2 · Status',
                left: 'แก้ไขสถานะโครงการ',
                dbl: '—',
                right: '—',
                admin: true,
              },
              {
                col: '3 · Progress %',
                left: 'แก้ไขตัวเลข % ความก้าวหน้า',
                dbl: '—',
                right: '—',
                admin: true,
              },
              {
                col: '4 · ชื่อโครงการ',
                left: 'กรองตามโครงการ (กดซ้ำเพื่อยกเลิก)',
                dbl: 'เปิดรายละเอียดโครงการ',
                right: 'แสดงชื่อเต็ม',
                admin: false,
              },
              {
                col: '5 · ลูกค้า',
                left: 'กรองตามลูกค้า (กดซ้ำเพื่อยกเลิก)',
                dbl: 'เปิดรายละเอียดโครงการ',
                right: 'เปิด Contact card',
                admin: false,
              },
              {
                col: '7 · Billed  /  8 · Received  /  12 · AP Pending',
                left: 'กำหนดค่าตั้งต้น (ข้อมูลก่อนเข้าระบบ)',
                dbl: '—',
                right: '—',
                admin: true,
              },
              {
                col: '16 · To Spend',
                left: 'แก้ไขยอดที่คาดว่าจะต้องใช้',
                dbl: '—',
                right: '—',
                admin: true,
              },
              {
                col: '26 · หมายเหตุ',
                left: 'แก้ไขหมายเหตุโครงการ',
                dbl: '—',
                right: '—',
                admin: true,
              },
              {
                col: '27 · วันที่ส่งงาน',
                left: 'กำหนด / แก้ไขวันที่ส่งงาน',
                dbl: '—',
                right: '—',
                admin: true,
              },
            ];
            const CELL = {
              padding: '8px 16px',
              fontSize: '0.88rem',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              verticalAlign: 'top',
            };
            const HEAD = {
              ...CELL,
              fontWeight: 700,
              backgroundColor: '#f1f5f9',
              fontSize: '0.8rem',
              color: '#475569',
              letterSpacing: '0.04em',
            };
            return (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[
                      'Column',
                      'คลิกซ้าย 🖱',
                      'ดับเบิลคลิก 🖱🖱',
                      'คลิกขวา 🖱',
                    ].map((h) => (
                      <th key={h} style={{ ...HEAD, textAlign: 'left' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={i}
                      style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}
                    >
                      <td
                        style={{
                          ...CELL,
                          fontWeight: 600,
                          color: '#1e3a5f',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {r.col}
                        {r.admin && (
                          <Box
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: '0.72rem',
                              bgcolor: '#fef3c7',
                              color: '#92400e',
                              px: 0.7,
                              py: 0.2,
                              borderRadius: 1,
                              fontWeight: 600,
                            }}
                          >
                            Admin
                          </Box>
                        )}
                      </td>
                      <td
                        style={{
                          ...CELL,
                          color: r.left === '—' ? '#cbd5e1' : '#0f172a',
                        }}
                      >
                        {r.left}
                      </td>
                      <td
                        style={{
                          ...CELL,
                          color: r.dbl === '—' ? '#cbd5e1' : '#0f172a',
                        }}
                      >
                        {r.dbl}
                      </td>
                      <td
                        style={{
                          ...CELL,
                          color: r.right === '—' ? '#cbd5e1' : '#0f172a',
                        }}
                      >
                        {r.right}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: '#f8fafc',
              borderTop: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              💡 ลาก header ขอบขวาเพื่อปรับความกว้าง column ·
              ลากพื้นที่ว่างในตารางเพื่อ scroll
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── right-click name tooltip ── */}
      {nameTooltip.open && (
        <Box
          sx={{
            position: 'fixed',
            top: nameTooltip.y + 10,
            left: nameTooltip.x,
            zIndex: 9999,
            bgcolor: '#1e293b',
            color: '#fff',
            fontSize: '0.91rem',
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            maxWidth: 360,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.5,
          }}
        >
          {nameTooltip.name}
        </Box>
      )}
    </Box>
  );
}
